/**
 * Lecteur de forme : octets d'un fichier Word → forme de chaque paragraphe du
 * corps et de chaque passage de texte, avec l'origine de chaque réglage.
 *
 * ## Pourquoi il existe (spec #1858, #1859)
 *
 * L'éditeur ne publie par ses lectures que la forme DIRECTE : un titre gras par
 * son style y est dit non gras. Le fichier Word que l'éditeur exporte en
 * mémoire, lui, porte tout — la forme directe dans le document, les styles
 * nommés et les défauts dans `styles.xml`. Ce module est la SEULE lecture de
 * forme du canvas : montrer la forme à l'agent, vérifier une écriture, trouver
 * un texte par sa forme, copier une forme.
 *
 * ## Ce qu'il est, et ce qu'il n'est pas
 *
 * Module PUR : aucune dépendance à l'éditeur, au navigateur ou au DOM — il se
 * teste sur des fichiers de référence et se déplacera tel quel dans un paquet
 * partagé le jour où le backend en aura besoin.
 *
 * Il résout la cascade que PEINT l'éditeur (mesurée sur SuperDoc 2.15.1, #1862),
 * du plus faible au plus fort : défauts du document → style de tableau →
 * style de paragraphe (et ceux dont il hérite) → niveau de liste → style de
 * caractère → forme directe. Les parts voisines vivent chacune dans son
 * module : `policeDeTheme.ts`, `numerotationDeForme.ts`, `styleDeTableau.ts`.
 *
 * ⚠️ Le juge est l'écran, pas la norme : le premier niveau qui définit un
 * réglage gagne, SANS la bascule que la norme prévoit pour le gras, l'italique
 * et leurs semblables quand plusieurs niveaux de style les portent — un gras
 * de style de caractère sur un gras de style de paragraphe est peint gras.
 * Source de la bascule : ISO/IEC 29500-1 §17.7.3 « Toggle Properties »,
 * https://learn.microsoft.com/en-us/openspecs/office_standards/ms-oi29500/f7130225-2368-48f3-acae-a9d278d0fb25
 * (vérifié 2026-10-02).
 *
 * Ce que le lecteur ne sait pas résoudre est rendu NON LISIBLE avec sa raison,
 * jamais deviné : un agent qui lit « non lisible » s'abstient, un agent qui
 * lit une valeur fausse écrit par-dessus.
 *
 * Le texte lu est celui que l'avocat voit : une insertion en attente y figure,
 * une suppression en attente n'y figure pas.
 *
 * ## Hors du corps
 *
 * Les en-têtes, les pieds de page et les notes de bas de page sont lus par la
 * même cascade, à part du corps (`FormeDeLActe.horsDuCorps`) : un en-tête ou un
 * pied est rattaché à sa zone par la section qui le référence
 * (`w:headerReference`, `w:footerReference`) et par les relations du document ;
 * une note, par son identifiant (`w:id`). Chaque paragraphe lu là porte sa
 * partie : c'est ce qui dit à une écriture où il se trouve. Les notes de fin ne
 * sont pas lues.
 *
 * ## La page, et les numéros de page
 *
 * La page de chaque section est lue telle que le fichier l'écrit
 * (`FormeDeLActe.sections` : `w:pgSz`, `w:pgMar`, en twips), et chaque
 * paragraphe dit les champs de numéro de page qu'il porte, dans l'ordre
 * (`FormeDeParagraphe.numerosDePage` : `PAGE`, `NUMPAGES`). Ce sont les deux
 * faits que les lectures de l'éditeur ne rendent pas : `blocks.list` rend un
 * pied « Page X / Y » en `Page  / ` (relevé du 2026-10-06, §14 de
 * `superdoc-document-api.md`).
 *
 * ⚠️ L'archive est ouverte par `JSZip.loadAsync` SANS la garde de taille de
 * `@hector/docx-integrity` (`checkDocxArchiveBounds`) : ses octets viennent de
 * l'export local de l'éditeur, pas d'un tiers. Un fichier REÇU (modèle, pièce
 * téléversée) passe par `lireAvecForme` (`lectureAvecForme.ts`), qui pose cette
 * garde avant de l'appeler.
 */

import { isWord, parseXml, XmlFault, type XmlNode } from '@hector/docx-integrity';
import type {
  CanvasFormatOrigin,
  CanvasHeaderFooterType,
  CanvasPageMarginSide,
  CanvasPageNumberField,
  CanvasPageOrientation,
  CanvasParagraphAlignment,
  CanvasParagraphFormat,
  CanvasRunFormat,
} from '@hector/shared-types';
import {
  CANVAS_PARAGRAPH_BORDER_SIDES,
  CANVAS_PARAGRAPH_CONTENT_KINDS,
  CANVAS_RUN_FORMAT_KEYS,
  type CanvasParagraphBorderSide,
  type CanvasParagraphContentKind,
  type CanvasRunFormatKey,
} from '@hector/shared-types';
import JSZip from 'jszip';
import { allume, attribut, enfant, lireAuNiveau, porteur, type Lecture, type Lu, type Niveau, type RaisonNonLisible } from './cascadeDeForme';
import { lireLaNumerotation, placerLeNiveauDeListe, type NumerotationDuDocument, type OrigineDeNumerotation } from './numerotationDeForme';
import { lectureDeLaPolice, lireLesPolicesDuTheme } from './policeDeTheme';
import { cellulesDuTableau, lignesDuTableau, niveauxDuStyleDeTableau, styleDuTableau, type CelluleDeTableau } from './styleDeTableau';
import { huitiemesEnPt, interligneEnMultiple, twipsEnCm, vingtiemesEnPt } from './unitesDeForme';

/** Un réglage lu : sa valeur et d'où elle vient, ou la raison pour laquelle il n'est pas rendu. */
export type Reglage<Valeur> =
  | { lisible: true; valeur: Valeur; origine: CanvasFormatOrigin }
  | { lisible: false; raison: RaisonNonLisible };

type ReglagesDe<Forme> = { [Cle in keyof Forme]: Reglage<Forme[Cle]> };

/**
 * Ce que la forme directe d'un passage pose, et ce que la cascade donne sans
 * elle (#1864). Une écriture qui EFFACE une forme directe a besoin des deux :
 * savoir s'il y a quelque chose à effacer (même quand la valeur résolue ne se
 * lit pas), et ce qui reparaîtrait dessous.
 */
export interface FormeSousLaMain {
  /** Les réglages que la forme directe du passage pose, que leur valeur se lise ou non. */
  aLaMain: CanvasRunFormatKey[];
  /** Surlignage et couleur que rendrait la cascade SANS la forme directe. */
  sousLaMain: { highlight: Reglage<string>; color: Reglage<string> };
  /**
   * Taille et souligné tels que le FICHIER les porte, là où l'écran les peint
   * autrement et où le passage les rend donc non lisibles (exposant, passage
   * déplacé par une révision). Une écriture se décide sur le fichier : c'est
   * lui que l'éditeur compare pour dire qu'elle ne change rien. Absent : le
   * passage dit déjà ce que porte le fichier.
   */
  auFichier?: { fontSizePt: Reglage<number>; underline: Reglage<boolean> };
  /**
   * La couleur de repli (`w:val`, `#RRGGBB`) d'une couleur de thème sans teinte,
   * quand `color` n'est pas lisible pour cette raison. Word l'écrit à
   * l'enregistrement : c'est la couleur que l'avocat voit tant que le thème n'a
   * pas changé. Elle sert à MONTRER la forme d'un document (`lectureAvecForme.ts`) ;
   * une écriture ne se décide pas sur elle. Absent : la couleur se lit, ou n'a
   * pas de repli sûr (teinte, ombre).
   */
  couleurDeTheme?: string;
}

/** Un passage : la plus longue suite de texte d'un paragraphe dont la forme est uniforme. */
export type FormeDePassage = { texte: string } & ReglagesDe<CanvasRunFormat> & FormeSousLaMain & {
  /**
   * Les éléments de `w:rPr` que les passages du fichier réunis ici portent à
   * la main, par leur nom (`w:` omis), hors style de caractère, langue et
   * révision — ceux des sept réglages compris. Absent : aucun. Il ne sépare pas
   * deux passages : la copie de forme y lit ce que le passage retenu du modèle
   * porte hors des sept réglages (`copieDeForme.ts`).
   */
  elementsALaMain?: string[];
};

/** Les règles d'interligne du fichier (`w:lineRule`) : multiple de ligne, hauteur exacte, hauteur minimale. */
export type RegleDInterligne = 'auto' | 'exact' | 'atLeast';

/**
 * Ce que la forme DIRECTE d'un paragraphe pose de retraits et d'espacement,
 * dans les unités du fichier (twips, vingtièmes de point, 240ᵉ de ligne), sans
 * résolution (#1866).
 *
 * WHY : l'éditeur REMPLACE tout `w:ind`, ou tout `w:spacing`, à chaque écriture
 * (`mesure-format-paragraphs`, R3 et E1, 2026-10-02 : poser le retrait droit
 * seul efface le retrait gauche posé à la main). Une écriture qui ne règle
 * qu'un attribut doit donc redonner les autres tels qu'ils sont — y compris un
 * interligne fixé en points, que la forme résolue rend non lisible.
 *
 * `nonRepris` nomme, par élément, les attributs posés à la main que l'éditeur
 * n'accepte pas en entrée et qu'il efface donc en écrivant cet élément
 * (`mesure-format-paragraphs`, S1 à S6, 2026-10-02 : `w:beforeAutospacing`,
 * `w:beforeLines`, `w:leftChars`… sont refusés à l'écriture et perdus), ainsi
 * que ceux dont la valeur n'est pas un nombre. Un paragraphe qui en porte ne
 * peut pas recevoir cette écriture sans qu'un réglage non demandé change.
 * `w:mirrorIndents`, élément à part de `w:pPr`, n'est pas suivi ici : ce
 * qu'une écriture de retraits en fait n'est pas mesuré.
 */
export interface FormeDirecteDeParagraphe {
  retraits: { left?: number; right?: number; firstLine?: number; hanging?: number };
  espacement: { before?: number; after?: number; line?: number; lineRule?: RegleDInterligne };
  nonRepris: { retraits: string[]; espacement: string[] };
}

/**
 * L'inventaire de ce qu'un paragraphe porte de forme posée à la main, par le
 * NOM des éléments du fichier (#1867). Une remise à zéro se décide et se juge
 * sur lui : la forme résolue ne dit pas d'où vient une valeur que le style
 * donnerait aussi, ni ce que le lecteur ne traduit pas (tabulations, ombrage,
 * saut de page avant).
 */
export interface InventaireDeFormeDirecte {
  /**
   * Les éléments du `w:pPr` direct, hors ceux qui ne sont pas de la forme posée
   * à la main : le style nommé (`w:pStyle`), la numérotation (`w:numPr`), la
   * section (`w:sectPr`), la révision de forme en attente (`w:pPrChange`) et
   * la structure (`w:pageBreakBefore`, `w:outlineLvl`, rendue par `structure`).
   */
  deParagraphe: string[];
  /**
   * La structure posée à la main sur le paragraphe : le saut de page avant
   * (`true`, ou `false` quand il est éteint contre un style) et le niveau de
   * plan, tel que le fichier l'écrit — `NaN` quand `w:outlineLvl` ne porte pas
   * un nombre. Une remise à zéro la redonne (`remiseAZero.ts`).
   */
  structure: { sautDePageAvant?: boolean; niveauDePlan?: number };
  /** Les côtés que le `w:pBdr` direct nomme (`top`, `left`, `bottom`, `right`, `between`, `bar`), tracés ou annulés. */
  cotesDeBordure: string[];
  /**
   * Les éléments des `w:rPr` directs des passages de texte, hors le style de
   * caractère (`w:rStyle`), la langue (`w:lang`), la vérification
   * (`w:noProof`) et la révision de forme en attente (`w:rPrChange`).
   */
  deTexte: string[];
  /** Vrai si un passage de texte porte un style de caractère (`w:rStyle`) : sa forme en dépend, quel que soit le style du paragraphe. */
  stylesDeCaractere: boolean;
  /**
   * Les révisions en attente que le paragraphe porte, selon ce qu'une remise à
   * zéro en ferait (mesures : en-tête de `remiseAZero.ts`).
   *
   * - `deParagraphe` : portée par `w:pPr` — `w:pPrChange`, ou une marque de
   *   paragraphe insérée, supprimée, déplacée ou à forme en attente
   *   (`w:pPr/w:rPr`). L'éditeur l'EFFACE en remettant le paragraphe à zéro.
   * - `deTexte` : portée par le texte — `w:rPrChange` n'importe où dans le
   *   paragraphe hors d'un texte supprimé en attente, que le passage porte du
   *   texte ou non, ou un déplacement en attente (`w:moveFrom`, `w:moveTo` et
   *   leurs bornes). L'éditeur REFUSE le plan qui retire la forme du texte ; la
   *   remise à zéro du paragraphe, elle, la garde.
   */
  revisionEnAttente: { deParagraphe: boolean; deTexte: boolean };
}

/**
 * Le trait d'un côté de bordure tel que le fichier l'écrit : son type
 * (`w:val` : `single`, `double`, `dotted`…), son épaisseur en huitièmes de
 * point (`w:sz`), son écart au texte en points (`w:space`) et sa couleur
 * (`w:color` : `RRGGBB` ou `auto`). Ce sont les valeurs qu'une écriture de ce
 * côté passe à l'éditeur (`reglagesDeParagraphe.ts`) : une copie de forme les
 * reprend telles quelles (#1869).
 */
export interface TraitDeBordure {
  type: string;
  huitiemes: number;
  ecart: number;
  couleur: string;
}

/**
 * Ce qu'un paragraphe porte et qu'une écriture de forme peut rencontrer
 * (#1868), relevé par `tests/ws-mocked/mesure-repli-de-lot.spec.ts` (L6,
 * SuperDoc 2.15.1, 2026-10-02, rejoué le 2026-10-04) :
 * - `signet` : un signet s'ouvre ou se ferme AU MILIEU du texte — du texte le
 *   précède et du texte le suit. Un signet vide en fin de paragraphe
 *   (`_GoBack`) ou qui entoure tout le texte (`_Toc…`) n'en est pas un :
 *   l'éditeur les écrit sans refus ;
 * - `signet_a_cheval` : un signet s'ouvre dans ce paragraphe et se ferme dans
 *   un autre, ou l'inverse ;
 * - `deplacement_en_attente`, `revision_de_forme_de_texte` (`w:rPrChange`),
 *   `revision_de_forme_de_paragraphe` (`w:pPrChange`) : une révision en attente
 *   qu'une écriture de forme modifierait. Une insertion ou une suppression en
 *   attente n'en est pas : une écriture de forme les laisse telles quelles ;
 * - `controle_verrouille` : un contrôle de contenu dont le contenu est
 *   verrouillé (`w:lock` à `sdtContentLocked` ou `contentLocked`). Sans verrou,
 *   ou quand seul son cadre est verrouillé (`sdtLocked`), l'éditeur l'écrit
 *   sans refus ;
 * - `plage_de_permission` : une plage de permission (`w:permStart`,
 *   `w:permEnd`) s'ouvre ou se ferme dans le paragraphe. L'éditeur refuse une
 *   forme de texte sur tout le paragraphe quand elle couvre une part du texte ;
 *   une plage autour de tout le texte n'est pas mesurée, et est marquée aussi.
 *
 * Ces marques ne décident d'aucun saut : c'est l'éditeur qui refuse. Elles
 * servent à dire POURQUOI en mots d'avocat (`repliDeLot.ts`), à soumettre à
 * part les étapes que l'éditeur risque de refuser, et à reconnaître le seul
 * cas où il accepte une écriture qui modifie une révision (`ecritureDeForme.ts`).
 */
export const MARQUES_DE_PARAGRAPHE = [
  'signet',
  'signet_a_cheval',
  'deplacement_en_attente',
  'revision_de_forme_de_texte',
  'revision_de_forme_de_paragraphe',
  'controle_verrouille',
  'plage_de_permission',
] as const;
export type MarqueDeParagraphe = (typeof MARQUES_DE_PARAGRAPHE)[number];

/**
 * Où se trouve un paragraphe lu hors du corps : la partie d'en-tête ou de pied
 * (`refId` : l'identifiant de relation par lequel la section la référence, celui
 * que l'éditeur emploie aussi), ou la note (`noteId` : son `w:id`).
 */
export type PartieHorsDuCorps =
  | { genre: 'header' | 'footer'; zone: CanvasHeaderFooterType; refId: string }
  | { genre: 'footnote'; noteId: string };

/**
 * La page d'une section, telle que son `w:sectPr` l'écrit, en twips (vingtièmes
 * de point : ISO/IEC 29500-1, élément `pgSz`, exemple A4 « 11907 twentieths of a
 * point wide »,
 * https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.pagesize,
 * vérifié 2026-10-07). `null` : le fichier n'écrit pas cette valeur, ou pas en
 * entier — rien n'en est supposé.
 */
export interface PageDeSection {
  /** `w:pgSz w:w` : la largeur de la feuille telle qu'elle est tournée (en paysage, le grand côté). */
  largeurTwips: number | null;
  /** `w:pgSz w:h`. */
  hauteurTwips: number | null;
  /** `w:pgSz w:orient`, quand le fichier l'écrit. L'éditeur l'écrit toujours (relevé du 2026-10-06, `mesure-socle-page-et-defauts`). */
  orientation: CanvasPageOrientation | null;
  /** `w:pgMar`, côté par côté. */
  margesTwips: Record<CanvasPageMarginSide, number | null>;
}

export interface FormeDeParagraphe {
  /** `w14:paraId` du fichier — l'identifiant de bloc que l'agent emploie. `null` si le fichier n'en porte pas. */
  nodeId: string | null;
  /**
   * L'identifiant que le fichier donne au paragraphe, quand `nodeId` est nul
   * parce qu'un autre paragraphe visé avec lui le porte aussi. Posé par la vue
   * visée ({@link corpsAvecSesCellules}, `acteVise` de `horsDuCorps.ts`),
   * jamais par le lecteur : un fichier peut porter deux fois un `w14:paraId`,
   * dans deux parties comme entre le corps et une cellule.
   */
  identifiantEnDouble?: string;
  /** La partie qui porte le paragraphe. Absente : le corps de l'acte. */
  partie?: PartieHorsDuCorps;
  /** Style nommé posé sur le paragraphe, sinon le style de paragraphe par défaut du document, sinon `null`. */
  styleId: string | null;
  styleNom: string | null;
  /** Vrai si le fichier définit le style `styleId` (`styles.xml`) : l'éditeur refuse de poser un style qu'il ne définit pas. */
  styleDefini: boolean;
  /**
   * La numérotation du paragraphe (`w:numPr`, liste `0` exclue) : posée à la
   * main, ou portée par son style — poser ce style sur un autre paragraphe le
   * numérote aussi (`mesure-copy-format`, S7, 2026-10-04).
   */
  numerotation: OrigineDeNumerotation;
  paragraphe: ReglagesDe<CanvasParagraphFormat>;
  /** Retraits et espacement que la forme directe pose, bruts : ce qu'une écriture doit redonner à l'éditeur. */
  aLaMain: FormeDirecteDeParagraphe;
  /**
   * Chaque côté de la bordure (`w:pBdr`), résolu seul par la cascade :
   * l'épaisseur de son trait en points, `0` sans trait. `paragraphe.border` et
   * `paragraphe.borderWidthPt` en sont le résumé.
   */
  bordures: Record<CanvasParagraphBorderSide, Reglage<number>>;
  /**
   * L'écart entre chaque trait et le texte : la valeur de `w:space` telle que
   * le fichier l'écrit — celle que l'écriture d'un côté passe à l'éditeur
   * (`reglagesDeParagraphe.ts`) —, résolue par la même cascade ; `0` sans
   * trait. Non lisible quand le côté tracé ne porte pas d'écart : rien n'en
   * est déduit.
   */
  ecartsDeBordure: Record<CanvasParagraphBorderSide, Reglage<number>>;
  /** Les mêmes côtés, tels que la cascade les donnerait SANS la forme directe : ce qui reparaît quand on efface un côté posé à la main. */
  borduresSousLaMain: Record<CanvasParagraphBorderSide, Reglage<number>>;
  /**
   * Le trait complet de chaque côté, résolu par la même cascade ; `null` : pas
   * de trait. Non lisible dès qu'un de ses quatre attributs manque ou ne se lit
   * pas (épaisseur, écart, couleur de thème) : rien n'en est déduit.
   */
  traitsDeBordure: Record<CanvasParagraphBorderSide, Reglage<TraitDeBordure | null>>;
  /** Les mêmes traits, tels que la cascade les donnerait SANS la forme directe. */
  traitsDeBordureSousLaMain: Record<CanvasParagraphBorderSide, Reglage<TraitDeBordure | null>>;
  /**
   * Le fond du paragraphe (`w:pPr/w:shd`), résolu par la cascade : sa couleur
   * (`RRGGBB` en capitales) ou `none` ({@link lireLeFondPose}). C'est le juge
   * d'un fond posé par `format_paragraphs` ; il est à part de `paragraphe`
   * parce que la lecture par origine (`formatOf`) ne le publie pas.
   */
  fond: Reglage<string>;
  /** Le même fond, tel que la cascade le donnerait SANS la forme directe : ce qui reparaît quand on efface un fond posé à la main. */
  fondSousLaMain: Reglage<string>;
  /** La cellule de tableau qui contient le paragraphe. Absente : il n'est pas dans une cellule. */
  cellule?: CelluleDuParagraphe;
  formeDirecte: InventaireDeFormeDirecte;
  /** Signets et révisions en attente que le paragraphe porte, dans l'ordre de {@link MARQUES_DE_PARAGRAPHE}. Vide : aucun. */
  porte: MarqueDeParagraphe[];
  /**
   * Les champs de numéro de page du paragraphe, dans l'ordre du texte : le numéro de la page (`PAGE`) ou le nombre
   * de pages (`NUMPAGES`). Vide : aucun. Les autres champs n'y figurent pas.
   */
  numerosDePage: CanvasPageNumberField[];
  /**
   * Ce que le paragraphe porte et qui n'est pas du texte ordinaire, dans l'ordre de `CANVAS_PARAGRAPH_CONTENT_KINDS` :
   * appel de note, champ, lien, signet qui entoure du texte, saut de ligne, tabulation, image suivie de texte,
   * insertion ou suppression en attente, ancre de commentaire. Vide : rien. Une écriture qui remplace TOUT le texte
   * du paragraphe ne les garde pas (§17.6 de `superdoc-document-api.md`) : c'est à elle de s'en garder.
   */
  horsTexte: CanvasParagraphContentKind[];
  /**
   * La forme de caractère posée à la main HORS des sept réglages des verbes de forme (petites capitales, barré,
   * exposant, espacement…) et les styles de caractère (`rStyle:<identifiant>`), par élément de `w:rPr`, dans l'ordre
   * du texte : le texte qui la porte, si elle couvre tout le texte du paragraphe, et si elle est sur son PREMIER
   * passage. `passages` ne la dit pas : il ne sépare pas deux textes qui ne diffèrent que par elle. Vide : aucune.
   */
  horsReglages: FormeHorsReglages[];
  passages: FormeDePassage[];
}

/**
 * La cellule de tableau (`w:tc`) d'un paragraphe : par quoi l'éditeur la vise,
 * son fond et son cadre. Mesures : `tests/ws-mocked/mesure-traits-et-fonds.spec.ts`
 * (SuperDoc 2.15.1, 2026-10-11).
 */
export interface CelluleDuParagraphe {
  /**
   * Le `w14:paraId` du PREMIER paragraphe de la cellule : l'éditeur nomme une
   * cellule d'après lui (`tc:<identifiant>`, C1), et ne la trouve ni par un
   * autre de ses paragraphes ni par l'identifiant nu (C7). `null` : la cellule
   * ne s'ouvre pas par un paragraphe (un tableau imbriqué d'abord), ou ce
   * paragraphe n'a pas d'identifiant — rien ne dit alors comment l'éditeur la
   * nomme.
   */
  premierParagraphe: string | null;
  /**
   * Le fond que la cellule pose à la main (`w:tcPr/w:shd`, {@link lireLeFondPose}),
   * `none` sans lui. Non lisible (`style_de_tableau`) quand elle n'en pose pas
   * et qu'un style de son tableau porte un fond de cellule : ce lecteur ne
   * résout pas les formats conditionnels d'un style de tableau pour `w:tcPr`.
   */
  fond: Reglage<string>;
  /** Vrai si `w:tcPr` porte un `w:shd`, quelle qu'en soit la valeur : l'éditeur n'efface que celui-là (C8, C9). */
  fondALaMain: boolean;
  /**
   * Le cadre de la case, côté par côté : l'épaisseur du trait en points, `0`
   * sans trait — le juge d'un cadre posé par `format_paragraphs`
   * (`cellBorder`). Origine `direct` : la case le pose (`w:tcBorders`) ;
   * `style` : il vient de `w:tblBorders` du tableau ou de son style ;
   * `default` : rien ne le trace. Non lisible (`style_de_tableau`) pour un côté
   * que la case ne pose pas quand sa place n'est pas établie (fusions, lignes
   * décalées), que sa ligne porte une exception de bordures, ou qu'un style du
   * tableau trace des traits par un format conditionnel.
   *
   * ⚠️ C'est le cadre de CETTE case : un côté partagé reste peint tant que la
   * case voisine le trace (mesuré, C9 et C10 de la sonde
   * `mesure-bordures-de-case`, 2026-10-12).
   */
  cadre: Record<CanvasParagraphBorderSide, Reglage<number>>;
}

/** Une forme de caractère hors des sept réglages, telle qu'un paragraphe la porte ({@link FormeDeParagraphe.horsReglages}). */
export interface FormeHorsReglages {
  /** L'élément de `w:rPr` (`smallCaps`, `strike`, `vertAlign`…), ou `rStyle:<identifiant>` pour un style de caractère. */
  element: string;
  /** Le texte qui la porte, passages réunis dans l'ordre. */
  texte: string;
  /** Vrai si tout le texte du paragraphe la porte. */
  partout: boolean;
  /** Vrai si le premier texte du paragraphe la porte : l'éditeur l'étend à tout texte réécrit. */
  enTete: boolean;
}

/** Un paragraphe de cellule de tableau, à quelque profondeur que ce soit : sa forme, et sa place parmi les paragraphes de son conteneur. */
export interface ParagrapheDeTableau extends FormeDeParagraphe {
  /** Nombre de paragraphes du corps qui le précèdent : le situe entre `paragraphes[rang - 1]` et `paragraphes[rang]`. */
  rang: number;
}

/**
 * Un style de paragraphe que le fichier définit (`styles.xml`), et la forme
 * qu'il donne. Poser ce style sur un paragraphe ne change que ce que ce
 * paragraphe ne pose pas à la main (`mesure-copy-format`, S2 et S5).
 */
export interface StyleDeParagraphe {
  /** `w:styleId` : ce qu'une écriture de style passe à l'éditeur. */
  id: string;
  /** `w:name` : le nom que Word montre à l'avocat. */
  nom: string | null;
  /** `w:basedOn`, que le fichier définisse ou non ce style-là. */
  heriteDe: string | null;
  /**
   * La forme d'un paragraphe du corps qui porte ce style et ne pose rien à la
   * main, résolue par la même cascade que les paragraphes de l'acte. Sans
   * identifiant ni passage : ce n'est pas un paragraphe du fichier.
   */
  forme: FormeDeParagraphe;
  /** La forme du texte de ce paragraphe-là. */
  texte: ReglagesDe<CanvasRunFormat>;
}

/** Un en-tête, un pied ou une note : ses paragraphes, lus comme ceux du corps. */
export interface FormeHorsDuCorps {
  partie: PartieHorsDuCorps;
  /**
   * Le chemin de la partie dans l'archive (`word/footer1.xml`), pour un en-tête
   * ou un pied : l'éditeur y range ses champs (`fields.list`, `storyId`
   * `footer:/word/footer1.xml`). Absent pour une note.
   */
  chemin?: string;
  /** Les paragraphes de la partie, dans son ordre, hors tableaux. */
  paragraphes: FormeDeParagraphe[];
  /** Ses paragraphes de cellule de tableau ; `rang` compte les paragraphes de la partie qui les précèdent. */
  paragraphesDeTableau: ParagrapheDeTableau[];
}

export interface FormeDeLActe {
  /**
   * Les paragraphes du corps hors de tout tableau, dans l'ordre du document.
   * Ceux des cellules sont lus aussi, à part (`paragraphesDeTableau`).
   */
  paragraphes: FormeDeParagraphe[];
  /**
   * En-têtes puis pieds, dans l'ordre des sections et de leurs références, puis
   * les notes de bas de page dans l'ordre du fichier. Une partie que deux
   * sections référencent pour la même zone n'y figure qu'une fois.
   */
  horsDuCorps: FormeHorsDuCorps[];
  /** Les styles de paragraphe que le fichier définit, dans l'ordre de `styles.xml`. */
  stylesDeParagraphe: StyleDeParagraphe[];
  /** La page de chaque section, dans l'ordre du document : celles que ferme un paragraphe, puis celle du corps. */
  sections: PageDeSection[];
  /**
   * Les paragraphes de cellule de tableau, dans l'ordre du document, avec leur
   * forme. À part de `paragraphes` : {@link corpsAvecSesCellules} les y remet à
   * leur place, pour ce qui vise ou juge le corps.
   */
  paragraphesDeTableau: ParagrapheDeTableau[];
}

/**
 * Un paragraphe tel que les outils de forme le visent, l'écrivent et le
 * jugent : sa forme, et s'il est dans une cellule de tableau.
 *
 * `enCellule` ne se lit qu'en deux endroits : la résolution d'une cible
 * (`cibleDeParagraphes.ts` — une cible « tout » ou une plage ne pose pas une
 * forme de PARAGRAPHE dans une cellule) et {@link voisinsSurLaPage}. Sa forme
 * se lit, s'écrit et se juge comme celle d'un autre (relevé du 2026-10-07, §16
 * de `superdoc-document-api.md`) : nulle branche « si tableau » ailleurs.
 */
export interface ParagrapheVise extends FormeDeParagraphe {
  /** Vrai pour un paragraphe de cellule de tableau, à quelque profondeur que ce soit. */
  enCellule: boolean;
}

/**
 * Ce qu'une cible de forme vise : les paragraphes du corps AVEC ceux de ses
 * cellules de tableau ({@link corpsAvecSesCellules}), ou ceux d'un en-tête,
 * d'un pied ou de notes (`acteVise`, `horsDuCorps.ts`), dans l'ordre du
 * document, en UNE liste.
 *
 * Un type à part de {@link FormeDeLActe}, que le compilateur ne laisse pas
 * passer pour lui ni l'inverse : dans l'acte lu, `paragraphes` n'a pas les
 * cellules ; ici il les a. La cible, les plans et la vérification du reçu
 * exigent cette vue.
 */
export interface ActeVise {
  /** Les paragraphes visables, dans l'ordre du document, cellules de tableau du corps comprises. */
  paragraphes: ParagrapheVise[];
  /**
   * Les paragraphes de cellule que cette vue ne rend PAS parmi `paragraphes` :
   * ceux des tableaux d'un en-tête, d'un pied ou d'une note, où ce que
   * l'éditeur accepte n'est pas mesuré. Comptés, jamais écrits (`table_cell`).
   * Toujours vide pour le corps.
   */
  cellulesNonVisees: ParagrapheDeTableau[];
  /** Les styles de paragraphe que le fichier définit ({@link FormeDeLActe.stylesDeParagraphe}). */
  stylesDeParagraphe: StyleDeParagraphe[];
}

/**
 * Rend nul l'identifiant de tout paragraphe qui le partage avec un autre de la
 * liste, en gardant la raison (`identifiantEnDouble` → `duplicate_node_id` au
 * reçu, jamais `no_node_id`).
 *
 * WHY : une cible, une écriture et la vérification retrouvent un paragraphe par
 * son identifiant ; porté deux fois, il ferait viser l'un et juger l'autre.
 * TOUS les porteurs sont écartés : dans une même story — le corps et ses
 * cellules —, rien ne dit lequel l'éditeur écrirait. (Entre deux PARTIES d'un
 * lieu, chacune a sa story : le premier porteur reste adressable, `acteVise`.)
 */
function sansIdentifiantEnDouble(paragraphes: readonly ParagrapheVise[]): ParagrapheVise[] {
  const porteurs = new Map<string, number>();
  for (const { nodeId } of paragraphes) if (nodeId !== null) porteurs.set(nodeId, (porteurs.get(nodeId) ?? 0) + 1);
  return paragraphes.map((lu) => (lu.nodeId === null || porteurs.get(lu.nodeId) === 1 ? lu : { ...lu, nodeId: null, identifiantEnDouble: lu.nodeId }));
}

/**
 * Le corps de l'acte tel que les outils de forme le visent : ses paragraphes
 * ET ceux de ses cellules de tableau, dans l'ordre du document, en une liste.
 *
 * WHY : pour l'éditeur, un paragraphe de cellule est un paragraphe comme un
 * autre — même identifiant que le fichier (`w14:paraId`), mêmes plans, mêmes
 * écritures hors plan (relevé du 2026-10-07, §16 de
 * `superdoc-document-api.md`). Rendu dans la liste que résolvent les cibles et
 * que relit la vérification, il est visé, écrit et jugé par le chemin commun.
 *
 * Un identifiant que deux de ces paragraphes portent (`w14:paraId` en double,
 * entre le corps et une cellule comme entre deux paragraphes du corps) n'en
 * nomme plus aucun ({@link sansIdentifiantEnDouble}).
 *
 * Les en-têtes, pieds et notes n'y sont pas : une cible les vise par son lieu
 * (`acteVise`, `horsDuCorps.ts`).
 */
export function corpsAvecSesCellules(acte: FormeDeLActe): ActeVise {
  const paragraphes: ParagrapheVise[] = [];
  let suivante = 0;
  // Une cellule de rang r précède le paragraphe de rang r du corps ; celles d'un tableau qui ferme le corps le suivent toutes.
  for (let rang = 0; rang <= acte.paragraphes.length; rang += 1) {
    for (; suivante < acte.paragraphesDeTableau.length && acte.paragraphesDeTableau[suivante]!.rang <= rang; suivante += 1) {
      const { rang: _rang, ...cellule } = acte.paragraphesDeTableau[suivante]!;
      paragraphes.push({ ...cellule, enCellule: true });
    }
    const duCorps = acte.paragraphes[rang];
    if (duCorps !== undefined) paragraphes.push({ ...duCorps, enCellule: false });
  }
  return { paragraphes: sansIdentifiantEnDouble(paragraphes), cellulesNonVisees: [], stylesDeParagraphe: acte.stylesDeParagraphe };
}

/**
 * Les suites de paragraphes qui se touchent sur la page, dans l'ordre du
 * document : un paragraphe de cellule n'est le voisin d'aucun autre (une
 * cellule, un tableau les séparent), et rompt la suite de ceux qui l'entourent.
 */
export function voisinsSurLaPage(acte: ActeVise): ParagrapheVise[][] {
  const suites: ParagrapheVise[][] = [[]];
  for (const lu of acte.paragraphes) {
    if (lu.enCellule) suites.push([]);
    else suites[suites.length - 1]!.push(lu);
  }
  return suites.filter((suite) => suite.length > 0);
}

/**
 * Les octets ne sont pas un fichier Word lisible. Le message dit ce qui
 * manque, en anglais : il part tel quel dans le reçu que lit le modèle.
 */
export class LectureDeFormeImpossible extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LectureDeFormeImpossible';
  }
}

const ESPACE_W14 = 'http://schemas.microsoft.com/office/word/2010/wordml';

// ── Lectures de texte (`w:rPr`) ──────────────────────────────────────────────

/** Propriété à bascule : l'élément présent sans valeur l'allume. */
function bascule(nom: string): Lecture<boolean> {
  return (proprietes) => {
    const noeud = enfant(proprietes, nom);
    return noeud ? { valeur: allume(attribut(noeud, 'val'), true) } : undefined;
  };
}

const lireSouligne: Lecture<boolean> = (proprietes) => {
  const noeud = enfant(proprietes, 'u');
  if (!noeud) return undefined;
  const valeur = attribut(noeud, 'val');
  if (valeur === undefined) return { nonLisible: 'valeur_inconnue' };
  return { valeur: valeur !== 'none' };
};

/**
 * Surlignage (`w:highlight`) : le nom de couleur du fichier, `none` compris —
 * c'est la valeur que Word écrit pour retirer un surlignage venu d'un style.
 * L'ombrage (`w:shd`) est un autre réglage : il n'est pas lu ici.
 */
const lireSurlignage: Lecture<string> = (proprietes) => {
  const noeud = enfant(proprietes, 'highlight');
  if (!noeud) return undefined;
  const couleur = attribut(noeud, 'val');
  return couleur ? { valeur: couleur } : { nonLisible: 'valeur_inconnue' };
};

/**
 * Couleur du texte (`w:color`) : `#RRGGBB` en capitales — la forme que l'agent
 * écrit — ou `auto`, la couleur automatique de Word (noir sur fond clair), qui
 * est aussi ce que Word applique quand aucun niveau ne nomme de couleur.
 * Une couleur de thème (`w:themeColor`, avec ses teintes) se calcule depuis
 * `theme1.xml` : elle n'est pas résolue ici, et son `w:val` de repli n'est pas
 * la couleur que l'avocat voit.
 */
const lireCouleur: Lecture<string> = (proprietes) => {
  const noeud = enfant(proprietes, 'color');
  if (!noeud) return undefined;
  if (attribut(noeud, 'themeColor') !== undefined) return { nonLisible: 'valeur_inconnue' };
  const valeur = attribut(noeud, 'val');
  if (valeur === 'auto') return { valeur };
  return valeur !== undefined && /^[0-9A-Fa-f]{6}$/.test(valeur) ? { valeur: `#${valeur.toUpperCase()}` } : { nonLisible: 'valeur_inconnue' };
};

/** Le repli (`w:val`) d'une couleur de thème sans teinte ni ombre ; toute autre couleur suit {@link lireCouleur}. */
const lireCouleurDeRepli: Lecture<string> = (proprietes) => {
  const noeud = enfant(proprietes, 'color');
  const valeur = attribut(noeud, 'val');
  const sansTeinte = attribut(noeud, 'themeTint') === undefined && attribut(noeud, 'themeShade') === undefined;
  if (noeud && attribut(noeud, 'themeColor') !== undefined && sansTeinte && valeur !== undefined && /^[0-9A-Fa-f]{6}$/.test(valeur)) return { valeur: `#${valeur.toUpperCase()}` };
  return lireCouleur(proprietes);
};

/**
 * L'élément de `w:rPr` qui porte chaque réglage de texte : sa présence dit
 * « posé à la main », lisible ou non. C'est l'élément que l'éditeur efface,
 * mesuré le 2026-10-02 (`mesure-format-text-police-taille-couleur`, E1 à E4) :
 * effacer la police retire tout `w:rFonts`, même réduit à un emplacement que
 * ce lecteur ne lit pas (`w:cs`) ; effacer la taille ne touche que `w:sz`, pas
 * un `w:szCs` seul.
 */
const ELEMENT_DU_REGLAGE: Readonly<Record<CanvasRunFormatKey, string>> = {
  bold: 'b',
  italic: 'i',
  underline: 'u',
  highlight: 'highlight',
  color: 'color',
  fontFamily: 'rFonts',
  fontSizePt: 'sz',
};

const lireTaille: Lecture<number> = (proprietes) => {
  const demiPoints = nombre(attribut(enfant(proprietes, 'sz'), 'val'));
  if (demiPoints === undefined) return undefined;
  return Number.isNaN(demiPoints) ? { nonLisible: 'valeur_inconnue' } : { valeur: demiPoints / 2 };
};

/** Exposant ou indice (`w:vertAlign`) : `baseline` ramène le passage sur la ligne. */
const lireExposantOuIndice: Lecture<boolean> = (proprietes) => {
  const position = attribut(enfant(proprietes, 'vertAlign'), 'val');
  return position === undefined ? undefined : { valeur: position === 'superscript' || position === 'subscript' };
};

// ── Lectures de paragraphe (`w:pPr`) ─────────────────────────────────────────

/**
 * Les jetons d'alignement du fichier. `justify` n'est pas un jeton Word : c'est
 * celui qu'écrit SuperDoc dans son export en mémoire (`@hector/docx-integrity`
 * le remplace par `both` avant toute livraison à Word, #1664).
 */
const ALIGNEMENTS: Readonly<Record<string, CanvasParagraphAlignment>> = {
  left: 'left',
  start: 'left',
  center: 'center',
  right: 'right',
  end: 'right',
  both: 'justify',
  justify: 'justify',
};

const lireAlignement: Lecture<CanvasParagraphAlignment> = (proprietes) => {
  const jeton = attribut(enfant(proprietes, 'jc'), 'val');
  if (jeton === undefined) return undefined;
  const alignement = ALIGNEMENTS[jeton];
  return alignement === undefined ? { nonLisible: 'valeur_inconnue' } : { valeur: alignement };
};

/** `undefined` : attribut absent. `NaN` : attribut présent mais non numérique. */
function nombre(texte: string | undefined): number | undefined {
  if (texte === undefined) return undefined;
  return /^-?\d+(\.\d+)?$/.test(texte) ? Number(texte) : Number.NaN;
}

function mesure(valeur: number | undefined, convertir: (brut: number) => number): Lu<number> {
  if (valeur === undefined) return undefined;
  return Number.isNaN(valeur) ? { nonLisible: 'valeur_inconnue' } : { valeur: convertir(valeur) };
}

/** Un attribut de `w:ind`. Chaque attribut suit sa propre cascade : un niveau peut ne poser que la gauche. */
function retrait(...noms: string[]): Lecture<number> {
  return (proprietes) => {
    const noeud = enfant(proprietes, 'ind');
    const present = noms.map((nom) => attribut(noeud, nom)).find((valeur) => valeur !== undefined);
    return mesure(nombre(present), twipsEnCm);
  };
}

/** Première ligne : `hanging` (retrait suspendu) l'emporte sur `firstLine` et se rend en négatif. */
const lirePremiereLigne: Lecture<number> = (proprietes) => {
  const noeud = enfant(proprietes, 'ind');
  const suspendu = nombre(attribut(noeud, 'hanging'));
  if (suspendu !== undefined) return mesure(suspendu, (twips) => 0 - twipsEnCm(twips));
  return mesure(nombre(attribut(noeud, 'firstLine')), twipsEnCm);
};

/** Espacement en vingtièmes de point. L'espacement « automatique » de Word n'a pas de valeur en points. */
function espacement(nom: 'before' | 'after'): Lecture<number> {
  return (proprietes) => {
    const noeud = enfant(proprietes, 'spacing');
    if (allume(attribut(noeud, `${nom}Autospacing`), false)) return { nonLisible: 'valeur_inconnue' };
    return mesure(nombre(attribut(noeud, nom)), vingtiemesEnPt);
  };
}

const REGLES_D_INTERLIGNE: readonly RegleDInterligne[] = ['auto', 'exact', 'atLeast'];

/**
 * Interligne (`w:spacing w:line`), en multiple de l'interligne simple. La règle
 * (`w:lineRule`) se lit sur le MÊME élément que la valeur, et vaut `auto` quand
 * il ne la porte pas : mesuré à l'écran (`mesure-format-paragraphs`, SANS-REGLE,
 * 2026-10-02 — `w:line="360"` sans règle est peint à 1,5 ligne). Un interligne
 * fixé en points n'est pas un multiple : non lisible.
 */
const lireInterligne: Lecture<number> = (proprietes) => {
  const noeud = enfant(proprietes, 'spacing');
  const ligne = nombre(attribut(noeud, 'line'));
  if (ligne === undefined) return undefined;
  if (Number.isNaN(ligne) || ligne <= 0) return { nonLisible: 'valeur_inconnue' };
  const regle = attribut(noeud, 'lineRule') ?? 'auto';
  if (regle !== 'auto') return { nonLisible: REGLES_D_INTERLIGNE.some((connue) => connue === regle) ? 'interligne_en_points' : 'valeur_inconnue' };
  return { valeur: interligneEnMultiple(ligne) };
};

/**
 * Un côté de la bordure de paragraphe (`w:pBdr`) : l'épaisseur de son trait, en
 * points ; `0` quand le côté est annulé (`nil`, `none`).
 *
 * Chaque côté suit sa propre cascade, et un côté annulé à la main efface celui
 * du style : mesuré à l'écran (`mesure-format-paragraphs-bordure-reset`,
 * 2026-10-02 — un `w:top` direct sur un style qui porte `w:left` peint les deux
 * traits ; `w:left w:val="nil"` ou `"none"` direct sur ce style n'en peint
 * aucun). C'est aussi ce que dit la norme pour chaque côté : « If this element
 * is omitted on a given paragraph, its value is determined by the setting
 * previously set at any level of the style hierarchy » — ISO/IEC 29500-1
 * (1re édition), élément `left` (§17.3.1.17), tel que le reproduit Microsoft
 * Learn, https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.leftborder
 * (vérifié 2026-10-02).
 *
 * Un trait sans épaisseur lisible (`w:sz` absent, nul ou non numérique) est
 * peint par l'éditeur d'un pixel (C2, C6) : aucune épaisseur n'en est rendue.
 * Les jetons `start` et `end` de la variante stricte du format sont lus comme
 * `left` et `right`.
 */
function coteDeBordure(cote: CanvasParagraphBorderSide, mesure: 'epaisseur' | 'ecart' = 'epaisseur'): Lecture<number> {
  const noms = cote === 'left' ? ['left', 'start'] : cote === 'right' ? ['right', 'end'] : [cote];
  return (proprietes) => {
    const bordure = enfant(proprietes, 'pBdr');
    const noeud = noms.map((nom) => enfant(bordure, nom)).find((candidat) => candidat !== undefined);
    if (!noeud) return undefined;
    if (mesure === 'epaisseur') return epaisseurDuTrait(noeud);
    const trait = attribut(noeud, 'val');
    if (trait === undefined) return { nonLisible: 'valeur_inconnue' };
    if (trait === 'nil' || trait === 'none') return { valeur: 0 };
    // Absent, rien n'en est déduit : le côté est dit non lisible.
    const ecart = nombre(attribut(noeud, 'space'));
    return ecart === undefined || Number.isNaN(ecart) || ecart < 0 ? { nonLisible: 'valeur_inconnue' } : { valeur: ecart };
  };
}

/** L'épaisseur, en points, du trait que pose un élément de côté (`w:top`, `w:insideH`…) ; `0` pour un côté annulé (`nil`, `none`). */
function epaisseurDuTrait(noeud: XmlNode): NonNullable<Lu<number>> {
  const trait = attribut(noeud, 'val');
  if (trait === undefined) return { nonLisible: 'valeur_inconnue' };
  if (trait === 'nil' || trait === 'none') return { valeur: 0 };
  const huitiemes = nombre(attribut(noeud, 'sz'));
  return huitiemes === undefined || Number.isNaN(huitiemes) || huitiemes <= 0 ? { nonLisible: 'valeur_inconnue' } : { valeur: huitiemesEnPt(huitiemes) };
}

/**
 * Le trait complet d'un côté de la bordure (voir {@link TraitDeBordure}), par
 * la même cascade et les mêmes noms de côté que {@link coteDeBordure}. `null` :
 * le côté est annulé. Un attribut absent n'est pas remplacé par un défaut : le
 * côté est dit non lisible.
 */
function traitDeBordure(cote: CanvasParagraphBorderSide): Lecture<TraitDeBordure | null> {
  const noms = cote === 'left' ? ['left', 'start'] : cote === 'right' ? ['right', 'end'] : [cote];
  return (proprietes) => {
    const bordure = enfant(proprietes, 'pBdr');
    const noeud = noms.map((nom) => enfant(bordure, nom)).find((candidat) => candidat !== undefined);
    if (!noeud) return undefined;
    const type = attribut(noeud, 'val');
    if (type === undefined) return { nonLisible: 'valeur_inconnue' };
    if (type === 'nil' || type === 'none') return { valeur: null };
    const [huitiemes, ecart, couleur] = [nombre(attribut(noeud, 'sz')), nombre(attribut(noeud, 'space')), attribut(noeud, 'color')];
    const lisible = huitiemes !== undefined && !Number.isNaN(huitiemes) && huitiemes > 0
      && ecart !== undefined && !Number.isNaN(ecart) && ecart >= 0
      && couleur !== undefined && (couleur === 'auto' || /^[0-9A-Fa-f]{6}$/.test(couleur))
      && attribut(noeud, 'themeColor') === undefined;
    return lisible ? { valeur: { type, huitiemes, ecart, couleur } } : { nonLisible: 'valeur_inconnue' };
  };
}

/**
 * Le fond que pose un élément `w:shd`, de paragraphe ou de cellule : la couleur
 * de `w:fill` en capitales (`554E94`), `none` quand elle est absente ou `auto`.
 * `undefined` : pas d'élément.
 *
 * Non lisible quand ce que l'écran montre ne se réduit pas à `w:fill` : un
 * motif (`w:val` autre que `clear` ou `nil` — `solid`, `pct25`… — mêle la
 * couleur de `w:color` à celle du fond), ou un `w:fill` qui n'est pas une
 * couleur hexadécimale. Un fond de thème (`w:themeFill`) est lu par son
 * `w:fill`, que Word écrit à côté (non mesuré à l'écran).
 *
 * Mesuré sur l'éditeur (`tests/ws-mocked/mesure-traits-et-fonds.spec.ts`,
 * SuperDoc 2.15.1, 2026-10-11) : `w:fill="auto"` posé à la main contre le fond
 * d'un style ne peint aucun fond (F10) ; l'éditeur écrit `w:val="clear"` avec
 * le fond d'une cellule (C2) et, pour un paragraphe, les seuls attributs qu'on
 * lui donne (F1, F15).
 */
export function lireLeFondPose(shd: XmlNode | undefined): Lu<string> {
  if (shd === undefined) return undefined;
  const motif = attribut(shd, 'val');
  if (motif !== undefined && motif !== 'clear' && motif !== 'nil') return { nonLisible: 'valeur_inconnue' };
  const couleur = attribut(shd, 'fill');
  if (couleur === undefined || couleur === 'auto') return { valeur: 'none' };
  return /^[0-9A-Fa-f]{6}$/.test(couleur) ? { valeur: couleur.toUpperCase() } : { nonLisible: 'valeur_inconnue' };
}

const lireLeFondDuParagraphe: Lecture<string> = (proprietes) => lireLeFondPose(enfant(proprietes, 'shd'));

/** Vrai si un style de tableau porte un fond de cellule, pour tout le tableau (`w:tcPr`) ou par un format conditionnel (`w:tblStylePr/w:tcPr`). */
function porteUnFondDeCellule(noeud: XmlNode): boolean {
  return noeud.children.some((piece) => (isWord(piece, 'tcPr') ? enfant(piece, 'shd') !== undefined : isWord(piece, 'tblStylePr') && porteUnFondDeCellule(piece)));
}

/** La cellule d'un paragraphe (voir {@link CelluleDuParagraphe}). */
function celluleDuParagraphe(cellule: CelluleDeTableau, styleDeTableau: readonly StyleNomme[]): CelluleDuParagraphe {
  const premierBloc = cellule.noeud.children.find((piece) => isWord(piece, 'p') || isWord(piece, 'tbl'));
  const shd = enfant(enfant(cellule.noeud, 'tcPr'), 'shd');
  const pose = lireLeFondPose(shd);
  const fond: Reglage<string> = pose === undefined
    ? (styleDeTableau.some((style) => porteUnFondDeCellule(style.noeud)) ? { lisible: false, raison: 'style_de_tableau' } : { lisible: true, valeur: 'none', origine: 'default' })
    : 'valeur' in pose ? { lisible: true, valeur: pose.valeur, origine: 'direct' } : { lisible: false, raison: pose.nonLisible };
  return {
    premierParagraphe: premierBloc !== undefined && isWord(premierBloc, 'p') ? premierBloc.attributes[`{${ESPACE_W14}}paraId`] ?? null : null,
    fond,
    fondALaMain: shd !== undefined,
    cadre: cadreDeLaCase(cellule, styleDeTableau),
  };
}

/** Vrai si un style de tableau trace des traits par un format conditionnel (`w:tblStylePr`) ou sur ses cases (`w:tcPr/w:tcBorders`). */
function porteUnCadreConditionnel(style: XmlNode): boolean {
  const borduresDe = (porteur: XmlNode): boolean => enfant(enfant(porteur, 'tcPr'), 'tcBorders') !== undefined || enfant(enfant(porteur, 'tblPr'), 'tblBorders') !== undefined;
  return enfant(enfant(style, 'tcPr'), 'tcBorders') !== undefined || style.children.some((piece) => isWord(piece, 'tblStylePr') && borduresDe(piece));
}

/**
 * Le cadre d'une case, côté par côté (voir {@link CelluleDuParagraphe.cadre}).
 *
 * Mesuré sur l'éditeur (`tests/ws-mocked/mesure-bordures-de-case.spec.ts`,
 * SuperDoc 2.15.1, 2026-10-12) : un côté que la case pose (`w:tcBorders`)
 * passe devant celui du tableau et devant celui du style — annulé (`nil`,
 * `none`), il n'est plus peint (C1 à C4, S1, S2) ; un tableau que rien
 * n'encadre n'est pas peint encadré (départ de la sonde, case « sans cadre ») ;
 * les côtés du bord du tableau suivent `top`, `left`, `bottom`, `right` de
 * `w:tblBorders`, les autres `insideH` et `insideV` (G1, G2).
 */
function cadreDeLaCase(cellule: CelluleDeTableau, styleDeTableau: readonly StyleNomme[]): CelluleDuParagraphe['cadre'] {
  const poses = enfant(enfant(cellule.noeud, 'tcPr'), 'tcBorders');
  const ligne = lignesDuTableau(cellule.tableau).find(({ cellules }) => cellules.includes(cellule.noeud))?.ligne;
  // Non mesurés : la place d'une case dans un tableau à fusions, l'exception de bordures d'une ligne, les traits d'un format conditionnel.
  const nonEtabli = cellule.positionIncertaine
    || enfant(enfant(ligne, 'tblPrEx'), 'tblBorders') !== undefined
    || styleDeTableau.some((style) => porteUnCadreConditionnel(style.noeud));
  const duTableau = [cellule.tableau, ...styleDeTableau.map((style) => style.noeud)].map((porteur) => enfant(enfant(porteur, 'tblPr'), 'tblBorders'));
  const auBord: Record<CanvasParagraphBorderSide, boolean> = { top: cellule.premiereLigne, left: cellule.premiereColonne, bottom: cellule.derniereLigne, right: cellule.derniereColonne };
  const trait = (bordures: XmlNode | undefined, noms: readonly string[]): Lu<number> => {
    const noeud = noms.map((nom) => enfant(bordures, nom)).find((candidat) => candidat !== undefined);
    return noeud === undefined ? undefined : epaisseurDuTrait(noeud);
  };
  const rendre = (lu: NonNullable<Lu<number>>, origine: CanvasFormatOrigin): Reglage<number> => ('valeur' in lu ? { lisible: true, valeur: lu.valeur, origine } : { lisible: false, raison: lu.nonLisible });
  const cote = (nom: CanvasParagraphBorderSide): Reglage<number> => {
    const noms = nom === 'left' ? ['left', 'start'] : nom === 'right' ? ['right', 'end'] : [nom];
    const pose = trait(poses, noms);
    if (pose !== undefined) return rendre(pose, 'direct');
    if (nonEtabli) return { lisible: false, raison: 'style_de_tableau' };
    const nomsAuTableau = auBord[nom] ? noms : [nom === 'top' || nom === 'bottom' ? 'insideH' : 'insideV'];
    for (const bordures of duTableau) {
      const lu = trait(bordures, nomsAuTableau);
      if (lu !== undefined) return rendre(lu, 'style');
    }
    return { lisible: true, valeur: 0, origine: 'default' };
  };
  return { top: cote('top'), left: cote('left'), bottom: cote('bottom'), right: cote('right') };
}

/** L'origine la plus forte parmi celles des côtés : un seul côté posé à la main suffit à dire la bordure « à la main ». */
const ORIGINES_DE_LA_PLUS_FORTE = ['direct', 'style', 'default'] as const satisfies readonly CanvasFormatOrigin[];

/**
 * Le résumé des quatre côtés : `border` nomme les côtés tracés (`none`, `box`,
 * ou ces côtés joints par `+`), `borderWidthPt` leur épaisseur commune — `0`
 * sans bordure, non lisible quand ils n'ont pas la même. Un côté non lisible
 * rend les deux non lisibles : la bordure n'est jamais devinée.
 */
export function resumerLaBordure(cotes: Record<CanvasParagraphBorderSide, Reglage<number>>): { border: Reglage<string>; borderWidthPt: Reglage<number> } {
  const lus: Array<{ cote: CanvasParagraphBorderSide; epaisseur: number; origine: CanvasFormatOrigin }> = [];
  for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
    const lu = cotes[cote];
    if (!lu.lisible) return { border: { lisible: false, raison: lu.raison }, borderWidthPt: { lisible: false, raison: lu.raison } };
    lus.push({ cote, epaisseur: lu.valeur, origine: lu.origine });
  }
  const origine = ORIGINES_DE_LA_PLUS_FORTE.find((candidate) => lus.some((lu) => lu.origine === candidate)) ?? 'default';
  const traces = lus.filter((lu) => lu.epaisseur > 0);
  const valeur = traces.length === 0 ? 'none' : traces.length === CANVAS_PARAGRAPH_BORDER_SIDES.length ? 'box' : traces.map((lu) => lu.cote).join('+');
  const epaisseurs = new Set(traces.map((lu) => lu.epaisseur));
  return {
    border: { lisible: true, valeur, origine },
    borderWidthPt: epaisseurs.size > 1 ? { lisible: false, raison: 'epaisseurs_differentes' } : { lisible: true, valeur: traces[0]?.epaisseur ?? 0, origine },
  };
}

/** Les éléments de `w:pPr` qui ne sont pas de la forme posée à la main : l'éditeur les garde en remettant le paragraphe à zéro. */
const HORS_FORME_DE_PARAGRAPHE: ReadonlySet<string> = new Set(['pStyle', 'numPr', 'sectPr', 'pPrChange', 'pageBreakBefore', 'outlineLvl']);
/** Les éléments d'un déplacement en attente : le passage déplacé, et les bornes de sa plage. */
const ELEMENTS_DE_DEPLACEMENT: readonly string[] = ['moveFrom', 'moveTo', 'moveFromRangeStart', 'moveFromRangeEnd', 'moveToRangeStart', 'moveToRangeEnd'];

/** Vrai si le paragraphe porte, hors de son `w:pPr` et d'un texte supprimé en attente, un élément `w:` que `garde` reconnaît. */
function porteDansLeTexte(noeud: XmlNode, garde: (local: string) => boolean): boolean {
  return noeud.children.some((piece) => {
    if (isWord(piece, 'pPr') || isWord(piece, 'del')) return false;
    return garde(piece.local) || porteDansLeTexte(piece, garde);
  });
}
/** Les éléments de `w:rPr` qui ne sont pas de la forme posée à la main. */
const HORS_FORME_DE_TEXTE: ReadonlySet<string> = new Set(['rStyle', 'lang', 'noProof', 'rPrChange']);
/** Les marques de révision qu'un `w:rPr` de marque de paragraphe peut porter. */
const REVISIONS_DE_MARQUE: readonly string[] = ['ins', 'del', 'moveFrom', 'moveTo', 'rPrChange'];

/** L'inventaire de la forme posée à la main (voir {@link InventaireDeFormeDirecte}). */
function inventorierLaFormeDirecte(paragraphe: XmlNode, pPr: XmlNode | undefined, rPrDesPassages: ReadonlyArray<XmlNode | undefined>): InventaireDeFormeDirecte {
  const marque = enfant(pPr, 'rPr');
  // Une marque de paragraphe qui ne porte que sa révision n'est pas de la forme : seule la révision est relevée.
  const marqueSansForme = marque !== undefined && marque.children.every((piece) => REVISIONS_DE_MARQUE.includes(piece.local));
  const noms = (noeuds: readonly XmlNode[], hors: ReadonlySet<string>): string[] => [...new Set(noeuds.map((noeud) => noeud.local).filter((nom) => !hors.has(nom)))];
  const saut = enfant(pPr, 'pageBreakBefore');
  const niveau = nombre(attribut(enfant(pPr, 'outlineLvl'), 'val'));
  return {
    deParagraphe: noms((pPr?.children ?? []).filter((piece) => !(piece === marque && marqueSansForme)), HORS_FORME_DE_PARAGRAPHE),
    structure: {
      ...(saut === undefined ? {} : { sautDePageAvant: allume(attribut(saut, 'val'), true) }),
      ...(niveau === undefined ? {} : { niveauDePlan: niveau }),
    },
    cotesDeBordure: (enfant(pPr, 'pBdr')?.children ?? []).map((cote) => cote.local),
    deTexte: noms(rPrDesPassages.flatMap((rPr) => rPr?.children ?? []), HORS_FORME_DE_TEXTE),
    stylesDeCaractere: rPrDesPassages.some((rPr) => enfant(rPr, 'rStyle') !== undefined),
    revisionEnAttente: {
      deParagraphe: enfant(pPr, 'pPrChange') !== undefined || REVISIONS_DE_MARQUE.some((revision) => enfant(marque, revision) !== undefined),
      deTexte: porteDansLeTexte(paragraphe, (local) => local === 'rPrChange' || ELEMENTS_DE_DEPLACEMENT.includes(local)),
    },
  };
}

/** Les retraits et l'espacement que pose le `w:pPr` direct, bruts (voir {@link FormeDirecteDeParagraphe}). */
function formeDirecteDuParagraphe(pPr: XmlNode | undefined, allumeSousLaMain: (attributDEspacement: string) => boolean): FormeDirecteDeParagraphe {
  const lu = (noeud: XmlNode | undefined, ...noms: string[]): number | undefined => {
    const valeur = noms.map((nom) => nombre(attribut(noeud, nom))).find((candidat) => candidat !== undefined);
    return valeur === undefined || Number.isNaN(valeur) ? undefined : valeur;
  };
  const sansAbsents = <Forme extends Record<string, unknown>>(forme: Forme): Partial<Forme> => (
    Object.fromEntries(Object.entries(forme).filter(([, valeur]) => valeur !== undefined)) as Partial<Forme>
  );
  const [retraits, espacement] = [enfant(pPr, 'ind'), enfant(pPr, 'spacing')];
  const regleEcrite = attribut(espacement, 'lineRule');
  const regle = REGLES_D_INTERLIGNE.find((connue) => connue === regleEcrite);
  /** Les attributs `w:` de l'élément qui ne repartiront pas : hors de ceux que l'éditeur écrit, ou de valeur illisible. */
  const nonRepris = (noeud: XmlNode | undefined, repris: readonly string[]): string[] => (
    Object.keys(noeud?.attributes ?? {}).flatMap((cle) => {
      const nom = cle.startsWith(`{${noeud?.uri}}`) ? cle.slice(`{${noeud?.uri}}`.length) : cle;
      if (!repris.includes(nom)) {
        // Un espacement automatique ÉTEINT à la main (« 0 », courant dans les Word issus d'une page web) se perd
        // sans rien changer : l'éditeur peint le même écart avant et après (`mesure-format-paragraphs`, Z1,
        // 2026-10-02) — sauf si un style l'allume dessous, auquel cas l'effacer le rallumerait.
        const eteint = (nom === 'beforeAutospacing' || nom === 'afterAutospacing') && !allume(attribut(noeud, nom), true) && !allumeSousLaMain(nom);
        return eteint ? [] : [nom];
      }
      // Une règle d'interligne inconnue, ou sans interligne à qui s'appliquer, ne repart pas : l'éditeur n'écrit la règle qu'avec sa valeur.
      if (nom === 'lineRule') return regle === undefined || attribut(noeud, 'line') === undefined ? [nom] : [];
      return Number.isNaN(nombre(attribut(noeud, nom))) ? [nom] : [];
    })
  );
  return {
    retraits: sansAbsents({ left: lu(retraits, 'left', 'start'), right: lu(retraits, 'right', 'end'), firstLine: lu(retraits, 'firstLine'), hanging: lu(retraits, 'hanging') }),
    espacement: sansAbsents({ before: lu(espacement, 'before'), after: lu(espacement, 'after'), line: lu(espacement, 'line'), lineRule: regle }),
    nonRepris: {
      retraits: nonRepris(retraits, ['left', 'start', 'right', 'end', 'firstLine', 'hanging']),
      espacement: nonRepris(espacement, ['before', 'after', 'line', 'lineRule']),
    },
  };
}

// ── Cascade ─────────────────────────────────────────────────────────────────

/**
 * Résout un réglage : le premier niveau qui le définit gagne.
 *
 * `obstacle` nomme une part de la cascade que ce lecteur ne résout pas et qui
 * se glisse SOUS la forme directe (style introuvable, liste introuvable) : la
 * forme directe reste lisible, tout le reste ne l'est plus.
 *
 * `defaut` est la valeur que Word applique quand aucun niveau ne dit rien ;
 * absent, le réglage est rendu non défini plutôt que supposé.
 */
function resoudre<Valeur>(
  niveaux: readonly Niveau[],
  lire: Lecture<Valeur>,
  obstacle: RaisonNonLisible | undefined,
  defaut: Valeur | undefined,
): Reglage<Valeur> {
  for (const niveau of niveaux) {
    const lu = lireAuNiveau(niveau, lire);
    if (lu !== undefined) {
      return 'valeur' in lu
        ? { lisible: true, valeur: lu.valeur, origine: niveau.origine }
        : { lisible: false, raison: lu.nonLisible };
    }
    if (niveau.origine === 'direct' && obstacle !== undefined) return { lisible: false, raison: obstacle };
  }
  return defaut === undefined
    ? { lisible: false, raison: 'non_defini' }
    : { lisible: true, valeur: defaut, origine: 'default' };
}

// ── Styles ──────────────────────────────────────────────────────────────────

interface StyleNomme {
  /** `w:type` : `paragraph`, `character`, `table`, `numbering`. */
  type: string | undefined;
  nom: string | null;
  heriteDe: string | undefined;
  pPr: XmlNode | undefined;
  rPr: XmlNode | undefined;
  /** L'élément `w:style` entier : un style de tableau y porte ses formats conditionnels. */
  noeud: XmlNode;
}

interface StylesDuDocument {
  parId: ReadonlyMap<string, StyleNomme>;
  paragrapheParDefaut: string | undefined;
  tableauParDefaut: string | undefined;
  defautsDeParagraphe: XmlNode | undefined;
  defautsDeTexte: XmlNode | undefined;
}

function lireLesStyles(racine: XmlNode | undefined): StylesDuDocument {
  const parId = new Map<string, StyleNomme>();
  let paragrapheParDefaut: string | undefined;
  let tableauParDefaut: string | undefined;
  for (const style of racine?.children ?? []) {
    if (!isWord(style, 'style')) continue;
    const id = attribut(style, 'styleId');
    if (!id) continue;
    parId.set(id, {
      type: attribut(style, 'type'),
      nom: attribut(enfant(style, 'name'), 'val') ?? null,
      heriteDe: attribut(enfant(style, 'basedOn'), 'val'),
      pPr: enfant(style, 'pPr'),
      rPr: enfant(style, 'rPr'),
      noeud: style,
    });
    if (attribut(style, 'type') === 'paragraph' && allume(attribut(style, 'default'), false)) paragrapheParDefaut = id;
    if (attribut(style, 'type') === 'table' && allume(attribut(style, 'default'), false)) tableauParDefaut = id;
  }
  const defauts = enfant(racine, 'docDefaults');
  return {
    parId,
    paragrapheParDefaut,
    tableauParDefaut,
    defautsDeParagraphe: enfant(enfant(defauts, 'pPrDefault'), 'pPr'),
    defautsDeTexte: enfant(enfant(defauts, 'rPrDefault'), 'rPr'),
  };
}

/**
 * Le style et ceux dont il hérite, du plus proche au plus lointain.
 *
 * Un `basedOn` qui pointe vers un style absent clôt la chaîne sans erreur : le
 * gabarit Hector fait hériter ses styles de `Normal`, qu'il ne définit pas, et
 * l'éditeur les affiche comme si l'héritage s'arrêtait là (mesuré 2026-10-02).
 */
function chaineDeStyles(styles: StylesDuDocument, depart: string): StyleNomme[] {
  const chaine: StyleNomme[] = [];
  const vus = new Set<string>();
  for (let id: string | undefined = depart; id !== undefined && !vus.has(id); ) {
    vus.add(id);
    const style = styles.parId.get(id);
    if (!style) break;
    chaine.push(style);
    id = style.heriteDe;
  }
  return chaine;
}

// ── Paragraphes et passages ─────────────────────────────────────────────────

/**
 * Les `w:r` du texte que l'avocat voit, dans l'ordre : on traverse liens,
 * insertions suivies et contrôles de contenu, on saute les suppressions
 * suivies (`w:del`, `w:moveFrom`).
 */
function* runsVisibles(noeud: XmlNode): Generator<XmlNode> {
  for (const candidat of noeud.children) {
    if (isWord(candidat, 'r')) yield candidat;
    else if (isWord(candidat, 'del') || isWord(candidat, 'moveFrom') || isWord(candidat, 'pPr')) continue;
    else yield* runsVisibles(candidat);
  }
}

/** Les `w:r` visibles qu'une révision en attente a DÉPLACÉS ici (`w:moveTo`). */
function* runsDeplaces(noeud: XmlNode): Generator<XmlNode> {
  for (const candidat of noeud.children) {
    if (isWord(candidat, 'moveTo')) yield* runsVisibles(candidat);
    else if (!isWord(candidat, 'r') && !isWord(candidat, 'del') && !isWord(candidat, 'moveFrom') && !isWord(candidat, 'pPr')) yield* runsDeplaces(candidat);
  }
}

/**
 * Retire de la forme d'un passage ce que l'éditeur PEINT autrement que le
 * fichier ne le porte (mesuré sur un acte de 1 701 paragraphes, 2026-10-02) :
 * - un exposant ou un indice est peint plus petit que sa taille — 13 points
 *   dans le fichier, 8,5 à l'écran ;
 * - un passage déplacé par une révision en attente est peint souligné.
 * Dire la valeur du fichier contredirait l'écran, dire celle de l'écran
 * contredirait le fichier : le réglage est rendu non lisible.
 */
function selonCeQuePeintLEcran(
  reglages: ReglagesDe<CanvasRunFormat>,
  exposantOuIndice: Reglage<boolean>,
  deplace: boolean,
): ReglagesDe<CanvasRunFormat> {
  // Un exposant qu'on ne sait pas lire (style absent du fichier) ne retire rien : la taille garde le sort
  // que lui a fait la cascade, comme les autres réglages du passage.
  const enExposant = exposantOuIndice.lisible && exposantOuIndice.valeur;
  return {
    ...reglages,
    fontSizePt: enExposant && reglages.fontSizePt.lisible ? { lisible: false, raison: 'exposant_ou_indice' } : reglages.fontSizePt,
    underline: deplace && reglages.underline.lisible ? { lisible: false, raison: 'revision_de_deplacement' } : reglages.underline,
  };
}

function texteDuRun(run: XmlNode): string {
  let texte = '';
  for (const piece of run.children) {
    if (isWord(piece, 't')) texte += piece.text;
    else if (isWord(piece, 'tab')) texte += '\t';
    else if (isWord(piece, 'br') || isWord(piece, 'cr')) texte += '\n';
    else if (isWord(piece, 'noBreakHyphen')) texte += '‑';
  }
  return texte;
}

/** Les verrous d'un contrôle de contenu (`ST_Lock`) devant lesquels l'éditeur refuse une forme de texte (`mesure-repli-de-lot`, L6) ; `sdtLocked`, lui, n'y fait rien. */
const VERROUS_DE_CONTENU: ReadonlySet<string> = new Set(['contentLocked', 'sdtContentLocked']);

/** Vrai si l'élément est un contrôle de contenu (`w:sdt`) dont le contenu est verrouillé. */
function estUnControleVerrouille(noeud: XmlNode): boolean {
  return isWord(noeud, 'sdt') && VERROUS_DE_CONTENU.has(attribut(enfant(enfant(noeud, 'sdtPr'), 'lock'), 'val') ?? '');
}

/**
 * Les signets et les révisions en attente que porte un paragraphe : voir {@link MarqueDeParagraphe}.
 *
 * @param dansUnControleVerrouille Le paragraphe est lui-même le contenu d'un contrôle verrouillé.
 */
function marquesDuParagraphe(noeud: XmlNode, dansUnControleVerrouille: boolean): MarqueDeParagraphe[] {
  const ouverts = new Set<string>();
  const fermes = new Set<string>();
  const vus = new Set<MarqueDeParagraphe>(dansUnControleVerrouille ? ['controle_verrouille'] : []);
  // Le paragraphe à plat, dans l'ordre : le texte que l'avocat voit, et les bornes de signet.
  const suite: Array<'texte' | 'borne'> = [];
  const parcourir = (parent: XmlNode, visible: boolean): void => {
    for (const candidat of parent.children) {
      if (isWord(candidat, 'bookmarkStart') || isWord(candidat, 'bookmarkEnd')) {
        (isWord(candidat, 'bookmarkStart') ? ouverts : fermes).add(attribut(candidat, 'id') ?? '');
        suite.push('borne');
      } else if (isWord(candidat, 'r')) {
        if (visible && texteDuRun(candidat) !== '') suite.push('texte');
      } else if (isWord(candidat, 'rPrChange')) vus.add('revision_de_forme_de_texte');
      else if (isWord(candidat, 'pPrChange')) vus.add('revision_de_forme_de_paragraphe');
      else if (estUnControleVerrouille(candidat)) vus.add('controle_verrouille');
      else if (isWord(candidat, 'permStart') || isWord(candidat, 'permEnd')) vus.add('plage_de_permission');
      if (ELEMENTS_DE_DEPLACEMENT.some((nom) => isWord(candidat, nom))) vus.add('deplacement_en_attente');
      // Ce qu'une révision de forme garde est la forme d'AVANT : rien de ce qu'elle contient n'est porté par le paragraphe.
      if (!isWord(candidat, 'rPrChange') && !isWord(candidat, 'pPrChange')) parcourir(candidat, visible && !isWord(candidat, 'del') && !isWord(candidat, 'moveFrom'));
    }
  };
  parcourir(noeud, true);
  const premierTexte = suite.indexOf('texte');
  const dernierTexte = suite.lastIndexOf('texte');
  if (suite.some((element, rang) => element === 'borne' && premierTexte >= 0 && premierTexte < rang && rang < dernierTexte)) vus.add('signet');
  if ([...ouverts].some((id) => !fermes.has(id)) || [...fermes].some((id) => !ouverts.has(id))) vus.add('signet_a_cheval');
  return MARQUES_DE_PARAGRAPHE.filter((marque) => vus.has(marque));
}

/** Les éléments de `w:rPr` qui portent les sept réglages des verbes de forme, jumeaux d'écriture complexe compris, et ceux qui ne sont pas de la forme. */
const ELEMENTS_DES_SEPT_REGLAGES: ReadonlySet<string> = new Set(['b', 'bCs', 'i', 'iCs', 'u', 'highlight', 'color', 'rFonts', 'sz', 'szCs', 'lang', 'noProof', 'rPrChange']);

/** La forme de caractère hors des sept réglages que porte un paragraphe : voir {@link FormeDeParagraphe.horsReglages}. */
function formesHorsReglages(noeud: XmlNode): FormeHorsReglages[] {
  const textes: Array<{ texte: string; elements: Set<string> }> = [];
  for (const run of runsVisibles(noeud)) {
    const texte = texteDuRun(run);
    if (texte === '') continue;
    const rPr = enfant(run, 'rPr');
    textes.push({
      texte,
      elements: new Set((rPr?.children ?? []).flatMap((element) => {
        if (element.local === 'rStyle') return [`rStyle:${attribut(element, 'val') ?? ''}`];
        return ELEMENTS_DES_SEPT_REGLAGES.has(element.local) ? [] : [element.local];
      })),
    });
  }
  const noms = [...new Set(textes.flatMap(({ elements }) => [...elements]))];
  return noms.map((element) => {
    const porteurs = textes.filter(({ elements }) => elements.has(element));
    return { element, texte: porteurs.map(({ texte }) => texte).join(''), partout: porteurs.length === textes.length, enTete: textes[0]?.elements.has(element) === true };
  });
}

/**
 * Ce qu'un paragraphe porte et qui n'est pas du texte ordinaire : voir {@link FormeDeParagraphe.horsTexte}.
 *
 * - Le `w:pPr` n'est pas parcouru : ses taquets (`w:tabs`) et la révision de sa marque de paragraphe ne sont pas du contenu.
 * - Un signet VIDE (`_GoBack`, posé là où était le curseur) n'entoure rien : il n'est pas relevé. Un signet ouvert ou
 *   fermé dans un autre paragraphe l'est dès que du texte de celui-ci est dedans.
 * - Une image n'est relevée que suivie de texte : en fin de paragraphe, une réécriture la garde (§17.1).
 */
function elementsHorsTexte(noeud: XmlNode): CanvasParagraphContentKind[] {
  const vus = new Set<CanvasParagraphContentKind>();
  /** Les signets ouverts dans ce paragraphe : vrai dès que du texte visible les suit. */
  const signetsOuverts = new Map<string, boolean>();
  let texteVu = false;
  let imageEnAttenteDeTexte = false;
  const noterDuTexte = (): void => {
    texteVu = true;
    if (imageEnAttenteDeTexte) vus.add('image_in_text');
    for (const id of signetsOuverts.keys()) signetsOuverts.set(id, true);
  };
  const parcourir = (parent: XmlNode, visible: boolean): void => {
    for (const piece of parent.children) {
      if (isWord(piece, 'pPr') || isWord(piece, 'rPr')) continue;
      if (isWord(piece, 'footnoteReference')) vus.add('footnote_reference');
      else if (isWord(piece, 'endnoteReference')) vus.add('endnote_reference');
      else if (isWord(piece, 'fldSimple') || isWord(piece, 'fldChar') || isWord(piece, 'instrText')) vus.add('field');
      else if (isWord(piece, 'hyperlink')) vus.add('hyperlink');
      else if (isWord(piece, 'br') || isWord(piece, 'cr')) vus.add('line_break');
      else if (isWord(piece, 'tab') || isWord(piece, 'ptab')) vus.add('tab');
      else if (isWord(piece, 'drawing') || isWord(piece, 'pict') || isWord(piece, 'object')) imageEnAttenteDeTexte = true;
      else if (isWord(piece, 'ins') || isWord(piece, 'del') || isWord(piece, 'moveFrom') || isWord(piece, 'moveTo')) vus.add('pending_tracked_change');
      else if (isWord(piece, 'commentRangeStart') || isWord(piece, 'commentRangeEnd') || isWord(piece, 'commentReference')) vus.add('comment_anchor');
      else if (isWord(piece, 'bookmarkStart')) signetsOuverts.set(attribut(piece, 'id') ?? '', false);
      else if (isWord(piece, 'bookmarkEnd')) {
        const id = attribut(piece, 'id') ?? '';
        // Fermé ici sans y avoir été ouvert : il entoure le texte qui le précède dans ce paragraphe.
        if (signetsOuverts.has(id) ? signetsOuverts.get(id) === true : texteVu) vus.add('bookmark');
        signetsOuverts.delete(id);
      }
      const dedans = visible && !isWord(piece, 'del') && !isWord(piece, 'moveFrom');
      if (isWord(piece, 'r') && dedans && texteDuRun(piece) !== '') noterDuTexte();
      // Une image contient ses propres paragraphes (zone de texte) : ils ne sont pas le texte de celui-ci.
      if (!isWord(piece, 'drawing') && !isWord(piece, 'pict') && !isWord(piece, 'object')) parcourir(piece, dedans);
    }
  };
  parcourir(noeud, true);
  // Ouvert ici et fermé plus loin : il entoure le texte qui le suit.
  if ([...signetsOuverts.values()].some((suiviDeTexte) => suiviDeTexte)) vus.add('bookmark');
  return CANVAS_PARAGRAPH_CONTENT_KINDS.filter((genre) => vus.has(genre));
}

/** Ce que la cascade d'un paragraphe lit hors de `styles.xml`. */
interface Entourage {
  lirePolice: Lecture<string>;
  numerotation: NumerotationDuDocument;
}

/** Ce qui situe un paragraphe dans la cascade, sous sa forme directe. */
interface SousLaFormeDirecte {
  /** Le style du paragraphe et ceux dont il hérite, du plus proche au plus lointain. */
  styleDeParagraphe: readonly StyleNomme[];
  /** Le style est POSÉ sur le paragraphe (`w:pStyle`), et non le style par défaut appliqué faute de mieux. */
  stylePose: boolean;
  /** Le style du tableau et ceux dont il hérite ; vide hors tableau ou sans style de tableau. */
  styleDeTableau: readonly StyleNomme[];
  cellule: CelluleDeTableau | undefined;
}

/**
 * Les niveaux d'une part (`pPr` ou `rPr`) SOUS la forme directe et le style de
 * caractère : style de paragraphe, style de tableau, défauts du document.
 *
 * Mesuré sur l'éditeur (2026-10-02) : le style POSÉ sur le paragraphe — et
 * toute sa chaîne — passe devant le style de tableau ; le style par défaut,
 * quand il ne s'applique que faute de style posé, passe derrière.
 */
function niveauxSousLaFormeDirecte(part: 'pPr' | 'rPr', situation: SousLaFormeDirecte, defauts: XmlNode | undefined): Niveau[] {
  const duParagraphe = situation.styleDeParagraphe.map((style) => porteur('style', style[part]));
  const duTableau = situation.cellule === undefined ? [] : niveauxDuStyleDeTableau(situation.styleDeTableau, situation.cellule, part);
  return [...(situation.stylePose ? [...duParagraphe, ...duTableau] : [...duTableau, ...duParagraphe]), porteur('default', defauts)];
}

/** Le premier mot d'une instruction de champ → le numéro de page qu'elle demande. Tout autre champ : `undefined`. */
export function numeroDePageDe(instruction: string): CanvasPageNumberField | undefined {
  const mot = instruction.trim().split(/\s+/u)[0]?.toUpperCase();
  return mot === 'PAGE' ? 'page' : mot === 'NUMPAGES' ? 'pages' : undefined;
}

/**
 * Les champs de numéro de page d'un paragraphe, dans l'ordre du texte.
 *
 * Un champ s'écrit de deux façons, et l'éditeur exporte les deux (relevé du
 * 2026-10-06, `mesure-socle-entetes-et-champs`) : `w:fldSimple w:instr="PAGE"`
 * pour un champ qu'il a inséré, et la suite `w:fldChar begin` → `w:instrText` →
 * `w:fldChar end` pour celui d'un gabarit. Word coupe parfois l'instruction
 * entre plusieurs `w:instrText` : elle est lue recollée. Un champ dans un champ
 * compte pour lui-même. Une suppression en attente n'est pas lue, comme le texte.
 */
function numerosDePageDuParagraphe(paragraphe: XmlNode): CanvasPageNumberField[] {
  const lus: CanvasPageNumberField[] = [];
  /** Les champs complexes ouverts : l'instruction de chacun, tant que son `separate` n'est pas passé. */
  const ouverts: Array<{ instruction: string; close: boolean }> = [];
  const retenir = (instruction: string): void => {
    const numero = numeroDePageDe(instruction);
    if (numero !== undefined) lus.push(numero);
  };
  const parcourir = (noeud: XmlNode): void => {
    for (const piece of noeud.children) {
      if (isWord(piece, 'del') || isWord(piece, 'moveFrom') || isWord(piece, 'pPr')) continue;
      if (isWord(piece, 'fldSimple')) {
        retenir(attribut(piece, 'instr') ?? '');
        parcourir(piece);
      } else if (isWord(piece, 'fldChar')) {
        const temps = attribut(piece, 'fldCharType');
        const courant = ouverts.at(-1);
        if (temps === 'begin') ouverts.push({ instruction: '', close: false });
        else if (courant !== undefined && !courant.close && (temps === 'separate' || temps === 'end')) {
          courant.close = true;
          retenir(courant.instruction);
        }
        if (temps === 'end') ouverts.pop();
      } else if (isWord(piece, 'instrText')) {
        const courant = ouverts.at(-1);
        if (courant !== undefined && !courant.close) courant.instruction += piece.text;
      } else parcourir(piece);
    }
  };
  parcourir(paragraphe);
  return lus;
}

/** Un attribut en twips : un entier, sinon `null`. */
function twips(noeud: XmlNode | undefined, nom: string): number | null {
  const brut = attribut(noeud, nom);
  return brut !== undefined && /^-?\d+$/u.test(brut) ? Number(brut) : null;
}

function orientationEcrite(brut: string | undefined): CanvasPageOrientation | null {
  return brut === 'portrait' || brut === 'landscape' ? brut : null;
}

/** La page d'un `w:sectPr`. */
function lireLaPageDeSection(section: XmlNode): PageDeSection {
  const format = enfant(section, 'pgSz');
  const marges = enfant(section, 'pgMar');
  return {
    largeurTwips: twips(format, 'w'),
    hauteurTwips: twips(format, 'h'),
    orientation: orientationEcrite(attribut(format, 'orient')),
    margesTwips: { top: twips(marges, 'top'), right: twips(marges, 'right'), bottom: twips(marges, 'bottom'), left: twips(marges, 'left') },
  };
}

/**
 * Lit la forme d'un paragraphe.
 *
 * @param cellule La cellule de tableau qui le contient ; `undefined` pour un paragraphe du corps.
 */
function lireLeParagraphe(
  noeud: XmlNode,
  styles: StylesDuDocument,
  { lirePolice, numerotation }: Entourage,
  cellule: CelluleDeTableau | undefined,
  dansUnControleVerrouille = false,
): FormeDeParagraphe {
  const pPr = enfant(noeud, 'pPr');
  const stylePose = attribut(enfant(pPr, 'pStyle'), 'val');
  const styleId = stylePose ?? styles.paragrapheParDefaut ?? null;
  const chaine = styleId === null ? [] : chaineDeStyles(styles, styleId);
  const styleIntrouvable: RaisonNonLisible | undefined = styleId !== null && chaine.length === 0 ? 'style_introuvable' : undefined;

  // Un tableau sans style posé, ou dont le style n'est pas défini dans le fichier, suit le style de tableau
  // par défaut : c'est ce que peint l'éditeur (mesuré 2026-10-02, tableau « au style absent » du Word importé).
  const stylePoseSurLeTableau = cellule === undefined ? undefined : styleDuTableau(cellule.tableau);
  const styleDeTableau = stylePoseSurLeTableau !== undefined && styles.parId.has(stylePoseSurLeTableau) ? stylePoseSurLeTableau : styles.tableauParDefaut;
  const tableau = cellule === undefined || styleDeTableau === undefined ? [] : chaineDeStyles(styles, styleDeTableau);

  const situation: SousLaFormeDirecte = { styleDeParagraphe: chaine, stylePose: stylePose !== undefined, styleDeTableau: tableau, cellule };

  const { niveaux: niveauxDeParagraphe, introuvable: listeIntrouvable, numerotation: origineDeNumerotation } = placerLeNiveauDeListe([
    porteur('direct', pPr),
    ...niveauxSousLaFormeDirecte('pPr', situation, styles.defautsDeParagraphe),
  ], numerotation);
  // Le niveau de liste porte retraits, alignement et espacement : introuvable, aucun de ces réglages n'est sûr
  // sous la forme directe.
  const obstacleDeParagraphe = styleIntrouvable ?? (listeIntrouvable ? 'numerotation' : undefined);

  // Les mêmes pour tous les passages du paragraphe : seuls la forme directe et le style de caractère changent d'un passage à l'autre.
  const niveauxDeTexteDuParagraphe = niveauxSousLaFormeDirecte('rPr', situation, styles.defautsDeTexte);
  const deplaces = new Set(runsDeplaces(noeud));
  const passages: FormeDePassage[] = [];
  const rPrDesPassages: Array<XmlNode | undefined> = [];
  let cleDuDernier = '';
  for (const run of runsVisibles(noeud)) {
    const texte = texteDuRun(run);
    if (texte === '') continue;
    const rPr = enfant(run, 'rPr');
    rPrDesPassages.push(rPr);
    // `w:rStyle` désigne un style par son identifiant : l'éditeur applique le
    // `rPr` du style trouvé, même si c'est un style de paragraphe (mesuré 2026-10-02).
    const styleDeCaractere = attribut(enfant(rPr, 'rStyle'), 'val');
    const chaineDeCaractere = styleDeCaractere === undefined ? [] : chaineDeStyles(styles, styleDeCaractere);
    const niveaux: Niveau[] = [
      porteur('direct', rPr),
      ...chaineDeCaractere.map((style) => porteur('style', style.rPr)),
      ...niveauxDeTexteDuParagraphe,
    ];
    const obstacle = styleDeCaractere !== undefined && chaineDeCaractere.length === 0 ? 'style_introuvable' : styleIntrouvable;
    const reglages: ReglagesDe<CanvasRunFormat> = {
      bold: resoudre(niveaux, bascule('b'), obstacle, false),
      italic: resoudre(niveaux, bascule('i'), obstacle, false),
      underline: resoudre(niveaux, lireSouligne, obstacle, false),
      highlight: resoudre(niveaux, lireSurlignage, obstacle, 'none'),
      color: resoudre(niveaux, lireCouleur, obstacle, 'auto'),
      // Sans police ni taille nulle part, Word retombe sur des valeurs propres
      // à l'application : rien de sûr à rendre.
      fontFamily: resoudre(niveaux, lirePolice, obstacle, undefined),
      fontSizePt: resoudre(niveaux, lireTaille, obstacle, undefined),
    };
    const repli = reglages.color.lisible ? undefined : resoudre(niveaux, lireCouleurDeRepli, obstacle, 'auto');
    const forme = selonCeQuePeintLEcran(reglages, resoudre(niveaux, lireExposantOuIndice, obstacle, false), deplaces.has(run));
    // Word découpe un texte de forme uniforme en plusieurs `w:r` (révisions,
    // correcteur, reprises d'édition) : deux voisins de même forme sont UN passage.
    // Sous la forme directe (#1864) : tous les niveaux sauf elle — style de
    // caractère, style de paragraphe, style de tableau, défauts. Un obstacle
    // (style introuvable) s'y glisse aussi : la cascade n'est alors pas résolue.
    const sansLaMain = (lire: Lecture<string>, defaut: string): Reglage<string> => (
      obstacle === undefined ? resoudre(niveaux.slice(1), lire, undefined, defaut) : { lisible: false, raison: obstacle }
    );
    const sousLaMain: FormeSousLaMain = {
      aLaMain: CANVAS_RUN_FORMAT_KEYS.filter((reglage) => enfant(rPr, ELEMENT_DU_REGLAGE[reglage]) !== undefined),
      sousLaMain: { highlight: sansLaMain(lireSurlignage, 'none'), color: sansLaMain(lireCouleur, 'auto') },
      ...(repli?.lisible === true && repli.valeur !== 'auto' ? { couleurDeTheme: repli.valeur } : {}),
      ...(forme.fontSizePt === reglages.fontSizePt && forme.underline === reglages.underline
        ? {}
        : { auFichier: { fontSizePt: reglages.fontSizePt, underline: reglages.underline } }),
    };
    const cle = JSON.stringify([forme, sousLaMain]);
    const dernier = passages.at(-1);
    const elements = (rPr?.children ?? []).map((element) => element.local).filter((element) => !HORS_FORME_DE_TEXTE.has(element));
    if (dernier && cle === cleDuDernier) {
      dernier.texte += texte;
      const reunis = [...new Set([...(dernier.elementsALaMain ?? []), ...elements])];
      if (reunis.length > 0) dernier.elementsALaMain = reunis;
    } else passages.push({ texte, ...forme, ...sousLaMain, ...(elements.length === 0 ? {} : { elementsALaMain: elements }) });
    cleDuDernier = cle;
  }

  const cotesDeLaBordure = (niveaux: readonly Niveau[], mesure: 'epaisseur' | 'ecart' = 'epaisseur'): Record<CanvasParagraphBorderSide, Reglage<number>> => ({
    top: resoudre(niveaux, coteDeBordure('top', mesure), obstacleDeParagraphe, 0),
    left: resoudre(niveaux, coteDeBordure('left', mesure), obstacleDeParagraphe, 0),
    bottom: resoudre(niveaux, coteDeBordure('bottom', mesure), obstacleDeParagraphe, 0),
    right: resoudre(niveaux, coteDeBordure('right', mesure), obstacleDeParagraphe, 0),
  });
  const bordures = cotesDeLaBordure(niveauxDeParagraphe);
  const traitsDeLaBordure = (niveaux: readonly Niveau[]): Record<CanvasParagraphBorderSide, Reglage<TraitDeBordure | null>> => ({
    top: resoudre(niveaux, traitDeBordure('top'), obstacleDeParagraphe, null),
    left: resoudre(niveaux, traitDeBordure('left'), obstacleDeParagraphe, null),
    bottom: resoudre(niveaux, traitDeBordure('bottom'), obstacleDeParagraphe, null),
    right: resoudre(niveaux, traitDeBordure('right'), obstacleDeParagraphe, null),
  });
  const nonLisibleSousLaMain = <Valeur>(raison: RaisonNonLisible): Record<CanvasParagraphBorderSide, Reglage<Valeur>> => (
    { top: { lisible: false, raison }, left: { lisible: false, raison }, bottom: { lisible: false, raison }, right: { lisible: false, raison } }
  );

  return {
    nodeId: noeud.attributes[`{${ESPACE_W14}}paraId`] ?? null,
    styleId,
    styleNom: chaine[0]?.nom ?? null,
    styleDefini: chaine.length > 0,
    numerotation: origineDeNumerotation,
    paragraphe: {
      alignment: resoudre(niveauxDeParagraphe, lireAlignement, obstacleDeParagraphe, 'left'),
      indentLeftCm: resoudre(niveauxDeParagraphe, retrait('left', 'start'), obstacleDeParagraphe, 0),
      indentRightCm: resoudre(niveauxDeParagraphe, retrait('right', 'end'), obstacleDeParagraphe, 0),
      indentFirstLineCm: resoudre(niveauxDeParagraphe, lirePremiereLigne, obstacleDeParagraphe, 0),
      spaceBeforePt: resoudre(niveauxDeParagraphe, espacement('before'), obstacleDeParagraphe, 0),
      spaceAfterPt: resoudre(niveauxDeParagraphe, espacement('after'), obstacleDeParagraphe, 0),
      // Sans interligne nulle part, aucun espacement n'est appliqué aux lignes — l'interligne simple. Source :
      // ISO/IEC 29500-1 §17.3.1.33, « If this setting is never specified in the style hierarchy, then the paragraph
      // shall have no spacing applied to its lines », https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.spacingbetweenlines
      // (vérifié 2026-10-02) ; c'est aussi ce que peint l'éditeur (`mesure-format-paragraphs`, TEMOIN : 16,87 px).
      lineSpacing: resoudre(niveauxDeParagraphe, lireInterligne, obstacleDeParagraphe, 1),
      ...resumerLaBordure(bordures),
    },
    bordures,
    ecartsDeBordure: cotesDeLaBordure(niveauxDeParagraphe, 'ecart'),
    // Sans la forme directe : un obstacle sous elle (style introuvable) rend ces côtés non lisibles, comme le reste.
    borduresSousLaMain: obstacleDeParagraphe === undefined
      ? cotesDeLaBordure(niveauxDeParagraphe.slice(1))
      : nonLisibleSousLaMain(obstacleDeParagraphe),
    traitsDeBordure: traitsDeLaBordure(niveauxDeParagraphe),
    traitsDeBordureSousLaMain: obstacleDeParagraphe === undefined
      ? traitsDeLaBordure(niveauxDeParagraphe.slice(1))
      : nonLisibleSousLaMain(obstacleDeParagraphe),
    fond: resoudre(niveauxDeParagraphe, lireLeFondDuParagraphe, obstacleDeParagraphe, 'none'),
    fondSousLaMain: obstacleDeParagraphe === undefined
      ? resoudre(niveauxDeParagraphe.slice(1), lireLeFondDuParagraphe, undefined, 'none')
      : { lisible: false, raison: obstacleDeParagraphe },
    ...(cellule === undefined ? {} : { cellule: celluleDuParagraphe(cellule, tableau) }),
    formeDirecte: inventorierLaFormeDirecte(noeud, pPr, rPrDesPassages),
    aLaMain: formeDirecteDuParagraphe(pPr, (attributDEspacement) => {
      const dessous = resoudre(niveauxDeParagraphe.slice(1), (proprietes) => {
        const valeur = attribut(enfant(proprietes, 'spacing'), attributDEspacement);
        return valeur === undefined ? undefined : { valeur: allume(valeur, false) };
      }, obstacleDeParagraphe, false);
      // Ce que le style donne ne se lit pas : rien ne prouve qu'il ne l'allume pas.
      return !dessous.lisible || dessous.valeur;
    }),
    porte: marquesDuParagraphe(noeud, dansUnControleVerrouille),
    numerosDePage: numerosDePageDuParagraphe(noeud),
    horsTexte: elementsHorsTexte(noeud),
    horsReglages: formesHorsReglages(noeud),
    passages,
  };
}

/**
 * Les `w:p` d'un conteneur dans l'ordre du document, chacun avec sa cellule de
 * tableau (`undefined` : paragraphe du corps), et s'il est le contenu d'un
 * contrôle de contenu verrouillé.
 */
function* paragraphesDansLOrdre(
  conteneur: XmlNode,
  cellule?: CelluleDeTableau,
  verrouille = false,
): Generator<{ noeud: XmlNode; cellule: CelluleDeTableau | undefined; verrouille: boolean }> {
  for (const candidat of conteneur.children) {
    if (isWord(candidat, 'p')) yield { noeud: candidat, cellule, verrouille };
    // Un tableau dans une cellule ouvre ses propres cellules : seul le tableau le plus proche situe un paragraphe.
    else if (isWord(candidat, 'tbl')) for (const situee of cellulesDuTableau(candidat)) yield* paragraphesDansLOrdre(situee.noeud, situee, verrouille);
    else yield* paragraphesDansLOrdre(candidat, cellule, verrouille || estUnControleVerrouille(candidat));
  }
}

/**
 * La forme que donne chaque style de paragraphe du fichier : celle d'un
 * paragraphe du corps qui ne porterait que lui, et un texte.
 *
 * WHY un paragraphe fabriqué plutôt qu'une seconde résolution : ce qu'un style
 * donne dépend de toute la cascade (styles hérités, niveau de liste, défauts,
 * thème), et deux résolutions finiraient par dire deux formes pour un même
 * paragraphe. L'élément est fabriqué dans l'espace de noms du style, celui où
 * la cascade lit ses attributs.
 */
function lireLesStylesDeParagraphe(styles: StylesDuDocument, entourage: Entourage): StyleDeParagraphe[] {
  return [...styles.parId].flatMap(([id, style]): StyleDeParagraphe[] => {
    if (style.type !== 'paragraph') return [];
    const { uri } = style.noeud;
    const element = (local: string, children: XmlNode[] = [], attributes: Record<string, string> = {}, text = ''): XmlNode => ({ name: `w:${local}`, local, uri, attributes, children, text });
    const lu = lireLeParagraphe(element('p', [
      element('pPr', [element('pStyle', [], { [`{${uri}}val`]: id })]),
      element('r', [element('t', [], {}, 'x')]),
    ]), styles, entourage, undefined);
    const texte = lu.passages[0];
    if (texte === undefined) {
      throw new Error(`lecteurDeForme: the paragraph built to read style "${id}" yielded no text run. This is a defect of lireLesStylesDeParagraphe; report it with this message.`);
    }
    const { bold, italic, underline, highlight, color, fontFamily, fontSizePt } = texte;
    return [{ id, nom: style.nom, heriteDe: style.heriteDe ?? null, forme: { ...lu, passages: [] }, texte: { bold, italic, underline, highlight, color, fontFamily, fontSizePt } }];
  });
}

async function lireLaPartie(archive: JSZip, chemin: string): Promise<XmlNode | undefined> {
  const partie = archive.file(chemin);
  if (!partie) return undefined;
  try {
    return parseXml(await partie.async('string'));
  } catch (erreur) {
    const detail = erreur instanceof XmlFault ? `${erreur.code}${erreur.location ? ` at ${erreur.location}` : ''}` : String(erreur);
    throw new LectureDeFormeImpossible(
      `the ${chemin} part of the exported Word file is not readable XML (${detail}). `
      + 'Nothing was modified. Run the same read again; if it fails again, the form of this document cannot be read.',
    );
  }
}

/**
 * Une partie ANNEXE de l'archive (thème, numérotation) : illisible, elle est
 * tenue pour absente. Les réglages qui en dépendent sont alors rendus non
 * lisibles, et le reste de l'acte est lu — un thème abîmé ne doit pas rendre
 * illisible la forme d'un acte sain.
 */
async function lireLaPartieAnnexe(archive: JSZip, chemin: string): Promise<XmlNode | undefined> {
  try {
    return await lireLaPartie(archive, chemin);
  } catch (erreur) {
    if (erreur instanceof LectureDeFormeImpossible) return undefined;
    throw erreur;
  }
}

const ESPACE_DES_RELATIONS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';

/** `w:type` d'une référence d'en-tête ou de pied → la zone, dans le vocabulaire des outils. */
const ZONES: Readonly<Record<string, CanvasHeaderFooterType>> = { default: 'primary', first: 'first_page', even: 'even_pages' };

/** Les `w:sectPr` du corps, dans l'ordre du document : ceux des paragraphes qui ferment une section, puis celui du corps. */
function* sectionsDuCorps(noeud: XmlNode): Generator<XmlNode> {
  for (const candidat of noeud.children) {
    if (isWord(candidat, 'sectPr')) yield candidat;
    else yield* sectionsDuCorps(candidat);
  }
}

/**
 * Les parties d'en-tête et de pied que les sections référencent : en-têtes puis
 * pieds, chacune une fois par zone. Une section sans référence reprend celles
 * de la précédente : elle n'ajoute rien.
 */
function partiesReferencees(sections: readonly XmlNode[]): Array<Extract<PartieHorsDuCorps, { refId: string }>> {
  const parties: Array<Extract<PartieHorsDuCorps, { refId: string }>> = [];
  for (const genre of ['header', 'footer'] as const) {
    for (const section of sections) {
      for (const reference of section.children) {
        if (!isWord(reference, `${genre}Reference`)) continue;
        const zone = ZONES[attribut(reference, 'type') ?? ''];
        const refId = reference.attributes[`{${ESPACE_DES_RELATIONS}}id`];
        if (zone === undefined || refId === undefined) continue;
        if (!parties.some((partie) => partie.genre === genre && partie.zone === zone && partie.refId === refId)) parties.push({ genre, zone, refId });
      }
    }
  }
  return parties;
}

/** Identifiant de relation → chemin de la partie dans l'archive, pour les relations internes du document. */
function ciblesDesRelations(relations: XmlNode | undefined): Map<string, string> {
  const cibles = new Map<string, string>();
  for (const relation of relations?.children ?? []) {
    const { '{}Id': id, '{}Target': cible, '{}TargetMode': mode } = relation.attributes;
    if (id === undefined || cible === undefined || mode === 'External') continue;
    cibles.set(id, cible.startsWith('/') ? cible.slice(1) : `word/${cible}`);
  }
  return cibles;
}

/**
 * Lit la forme de tous les paragraphes d'un fichier Word : ceux du corps, à part ceux des cellules de tableau
 * ({@link corpsAvecSesCellules} les réunit), et à part ceux des en-têtes, des pieds et des notes de bas de page.
 *
 * @param octets Le fichier `.docx` complet, tel que l'éditeur l'exporte en mémoire.
 * @throws {LectureDeFormeImpossible} si les octets ne sont pas une archive Word
 *   portant un `word/document.xml` lisible.
 */
export async function lireLaForme(octets: Uint8Array): Promise<FormeDeLActe> {
  let archive: JSZip;
  try {
    archive = await JSZip.loadAsync(octets);
  } catch (erreur) {
    throw new LectureDeFormeImpossible(
      `the ${octets.byteLength} exported bytes are not a readable Word (.docx) archive `
      + `(${erreur instanceof Error ? erreur.message : String(erreur)}). Nothing was modified. Run the same read again.`,
    );
  }
  const document = await lireLaPartie(archive, 'word/document.xml');
  const corps = enfant(document, 'body');
  if (!corps) {
    throw new LectureDeFormeImpossible(
      'the exported archive has no document body (word/document.xml with w:body). '
      + 'Nothing was modified. Run the same read again.',
    );
  }
  // Un fichier sans `styles.xml` reste lisible : seuls la forme directe et les
  // défauts de Word comptent alors, ce que la cascade rend d'elle-même.
  const styles = lireLesStyles(await lireLaPartie(archive, 'word/styles.xml'));
  // Thème et numérotation sont lus à leur chemin habituel dans l'archive, celui que l'éditeur exporte ;
  // absents ou illisibles, les réglages qui en dépendent sont rendus non lisibles.
  const entourage: Entourage = {
    lirePolice: lectureDeLaPolice(lireLesPolicesDuTheme(await lireLaPartieAnnexe(archive, 'word/theme/theme1.xml'))),
    numerotation: lireLaNumerotation(await lireLaPartieAnnexe(archive, 'word/numbering.xml')),
  };
  /** Les paragraphes d'un conteneur — corps, en-tête, pied, note —, ceux de ses tableaux à part. */
  const lireLeConteneur = (conteneur: XmlNode, partie?: PartieHorsDuCorps): Pick<FormeDeLActe, 'paragraphes' | 'paragraphesDeTableau'> => {
    const paragraphes: FormeDeParagraphe[] = [];
    const paragraphesDeTableau: ParagrapheDeTableau[] = [];
    for (const { noeud, cellule, verrouille } of paragraphesDansLOrdre(conteneur)) {
      const lu = { ...lireLeParagraphe(noeud, styles, entourage, cellule, verrouille), ...(partie === undefined ? {} : { partie }) };
      if (cellule === undefined) paragraphes.push(lu);
      else paragraphesDeTableau.push({ ...lu, rang: paragraphes.length });
    }
    return { paragraphes, paragraphesDeTableau };
  };

  const horsDuCorps: FormeHorsDuCorps[] = [];
  const cibles = ciblesDesRelations(await lireLaPartieAnnexe(archive, 'word/_rels/document.xml.rels'));
  // Les sections du corps, parcourues une fois : leurs références de parties, puis leur page.
  const sections = [...sectionsDuCorps(corps)];
  for (const partie of partiesReferencees(sections)) {
    const cible = cibles.get(partie.refId);
    const racine = cible === undefined ? undefined : await lireLaPartieAnnexe(archive, cible);
    if (racine !== undefined && cible !== undefined) horsDuCorps.push({ partie, chemin: cible, ...lireLeConteneur(racine, partie) });
  }
  for (const note of (await lireLaPartieAnnexe(archive, 'word/footnotes.xml'))?.children ?? []) {
    const noteId = attribut(note, 'id');
    // Les séparateurs sont des notes au fichier (`w:type`), pas des notes de l'avocat.
    if (!isWord(note, 'footnote') || noteId === undefined || (attribut(note, 'type') ?? 'normal') !== 'normal') continue;
    const partie: PartieHorsDuCorps = { genre: 'footnote', noteId };
    horsDuCorps.push({ partie, ...lireLeConteneur(note, partie) });
  }
  return {
    ...lireLeConteneur(corps),
    horsDuCorps,
    stylesDeParagraphe: lireLesStylesDeParagraphe(styles, entourage),
    sections: sections.map(lireLaPageDeSection),
  };
}
